import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUsers, FiFeather, FiAlertTriangle,
  FiVideo, FiCalendar, FiAnchor, FiBell, FiSearch,
  FiRefreshCw, FiDownload, FiCheck, FiTrendingUp,
  FiUserPlus, FiUserX, FiCreditCard, FiShield, FiHeart, FiFrown,
  FiX, FiTrash2, FiActivity, FiSend, FiMessageSquare, FiExternalLink, FiCheckCircle,
  FiChevronRight, FiChevronDown, FiChevronLeft, FiClock, FiMinus, FiPlus, FiSun, FiMoon
} from "react-icons/fi";
import { FaClock, FaUsers } from "react-icons/fa";
import { GiLotus } from "react-icons/gi";
import AdminDashboardLayout from "../../layouts/AdminDashboardLayout";
import BrandLoader from "../../components/BrandLoader";
import adminApi from "../../services/adminApi";
import { liveSessionAPI, practiceVideoAPI, dietPlanAPI, notificationAPI } from "../../services/api";
import { useDynamicPackages } from "../../hooks/useDynamicPackages";
import { packageService } from "../../services/packageService";

/* ─── Design tokens ────────────────────────────────────────────────────── */
const T = {
  orange:"#F29A0C", orange2:"#E97818",
  olive:"#6B8A5A",  olive2:"#8BA47A",
  dark:"#1F1F1F",   mid:"#555",
  muted:"#AAA",     light:"#CCC",
  border:"rgba(239,229,216,0.8)",
  green:"#22C55E",  greenDk:"#16A34A",
  red:"#E24B4A",    redDk:"#A32D2D",
};
const glass = {
  background:"rgba(255,255,255,0.82)",
  border:"1px solid rgba(255,255,255,0.95)",
  backdropFilter:"blur(32px)", WebkitBackdropFilter:"blur(32px)",
  boxShadow:"0 20px 60px rgba(0,0,0,0.06), inset 0 1px 0 #fff",
};

/* ─── Normalise API response fields ────────────────────────────────────── */
// ASP.NET Core may return PascalCase or camelCase   this handles both.
const normaliseStats = (d) => ({
  totalUsers:          d.totalUsers          ?? d.TotalUsers          ?? 0,
  activeUsers:         d.activeUsers         ?? d.ActiveUsers         ?? 0,
  inactiveUsers:       d.inactiveUsers       ?? d.InactiveUsers       ?? 0,
  onlineUsersCount:    d.onlineUsersCount    ?? d.OnlineUsersCount    ?? 0,
  deactivatedUsers:    d.deactivatedUsers    ?? d.DeactivatedUsers    ?? 0,
  totalAdmins:         d.totalAdmins         ?? d.TotalAdmins         ?? 0,
  totalSubscriptions:  d.totalSubscriptions  ?? d.TotalSubscriptions  ?? 0,
  activeSubscriptions: d.activeSubscriptions ?? d.ActiveSubscriptions ?? 0,
  totalRevenue:        d.totalRevenue        ?? d.TotalRevenue        ?? 0,
  totalPackages:       d.totalPackages       ?? d.TotalPackages       ?? 0,
  totalClasses:        d.totalClasses        ?? d.TotalClasses        ?? 0,
  totalDietPlans:      d.totalDietPlans      ?? d.TotalDietPlans      ?? 0,
  attendanceToday:         d.attendanceToday         ?? d.AttendanceToday         ?? 0,
  todayCheckedInCount:     d.todayCheckedInCount     ?? d.TodayCheckedInCount     ?? d.attendanceToday ?? d.AttendanceToday ?? 0,
  currentlyCheckedInCount: d.currentlyCheckedInCount ?? d.CurrentlyCheckedInCount ?? 0,
  todayCheckedOutCount:    d.todayCheckedOutCount    ?? d.TodayCheckedOutCount    ?? 0,
  newUsersThisMonth:   d.newUsersThisMonth   ?? d.NewUsersThisMonth   ?? 0,
  totalLiveSessions:   d.totalLiveSessions   ?? d.TotalLiveSessions   ?? 0,
  totalPracticeVideos: d.totalPracticeVideos ?? d.TotalPracticeVideos ?? 0,
  usersList:           d.usersList           ?? d.UsersList           ?? [],
  todayCheckedInUsers: d.todayCheckedInUsers ?? d.TodayCheckedInUsers ?? [],
  inactiveUsersList:   d.inactiveUsersList   ?? d.InactiveUsersList   ?? [],
  revenueByMonth: (d.revenueByMonth ?? d.RevenueByMonth ?? []).map(r => ({
    month:   r.month   ?? r.Month   ?? "",
    revenue: r.revenue ?? r.Revenue ?? 0,
  })),
  registrationsByMonth: (d.registrationsByMonth ?? d.RegistrationsByMonth ?? []).map(r => ({
    month: r.month ?? r.Month ?? "",
    count: r.count ?? r.Count ?? 0,
  })),
  videosByMonth: (d.videosByMonth ?? d.VideosByMonth ?? []).map(r => ({
    month: r.month ?? r.Month ?? "",
    count: r.count ?? r.Count ?? 0,
  })),
  sessionsByMonth: (d.sessionsByMonth ?? d.SessionsByMonth ?? []).map(r => ({
    month: r.month ?? r.Month ?? "",
    count: r.count ?? r.Count ?? 0,
  })),
  subscriptionMix:     d.subscriptionMix     ?? d.SubscriptionMix     ?? {},
  revenueBreakdown:    d.revenueBreakdown    ?? d.RevenueBreakdown    ?? {
    today: 0,
    threeDays: 0,
    oneWeek: 0,
    fifteenDays: 0,
    oneMonth: 0,
    dailyPoints: []
  },
  priorityAlerts:      d.priorityAlerts      ?? d.PriorityAlerts      ?? {},
});

/* ─── Sub-components ────────────────────────────────────────────────────── */
function Toast({ message }) {
  return (
    <motion.div
      initial={{ opacity:0, y:16, scale:0.95 }}
      animate={{ opacity:1, y:0, scale:1 }}
      exit={{ opacity:0, y:16 }}
      className="ad-toast fixed bottom-7 right-7 z-50 flex items-center gap-3 px-5 py-3.5 rounded-[16px] text-white text-[13px] font-semibold"
      style={{ background:"linear-gradient(135deg,#1F1F1F,#2D2D2D)", boxShadow:"0 16px 48px rgba(0,0,0,0.25)", fontFamily:"'Inter',sans-serif" }}>
      <FiCheck size={16} style={{ color:T.orange }} />{message}
    </motion.div>
  );
}

// ─── CUSTOM LUXURY DROPDOWN COMPONENT (PORTALED TO PREVENT MODAL CLIPPING) ────
function CustomDropdown({ value, onChange, options = [], label, icon: Icon, fullWidth = true, align = "left", searchable = true, placeholder = "Select..." }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 220, isUpward: false });
  const buttonRef = useRef(null);

  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const isUpward = spaceBelow < 280 && spaceAbove > spaceBelow;

    setMenuPos({
      top: isUpward ? Math.max(10, rect.top - 286) : rect.bottom + 6,
      left: align === "right" ? Math.max(10, rect.right - Math.max(rect.width, 220)) : rect.left,
      width: Math.max(rect.width, 200),
      isUpward,
    });
  }, [align]);

  const handleToggle = () => {
    if (!open) {
      calculatePosition();
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    calculatePosition();

    const handleWindowChange = () => {
      calculatePosition();
    };

    const handleClickOutside = (e) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(e.target) &&
        !e.target.closest(".custom-dropdown-portal-popover")
      ) {
        setOpen(false);
      }
    };

    window.addEventListener("scroll", handleWindowChange, true);
    window.addEventListener("resize", handleWindowChange);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleWindowChange, true);
      window.removeEventListener("resize", handleWindowChange);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, calculatePosition]);

  useEffect(() => {
    if (!open) setSearchTerm("");
  }, [open]);

  const selectedOption = (options || []).find((o) => String(o.value) === String(value));

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options || [];
    return (options || []).filter((o) =>
      String(o.label || "").toLowerCase().includes(searchTerm.toLowerCase().trim())
    );
  }, [options, searchTerm]);

  return (
    <div className={`relative ${fullWidth ? "w-full" : "inline-block"} text-left`}>
      <motion.button
        ref={buttonRef}
        type="button"
        whileTap={{ scale: 0.98 }}
        onClick={handleToggle}
        className={`flex items-center justify-between gap-2 ${fullWidth ? "w-full rounded-[12px] px-3.5 py-2 text-[13px] h-10" : "rounded-full px-4 py-2 text-[12px]"} font-semibold border cursor-pointer outline-none transition-all shadow-sm`}
        style={{
          background: open ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.85)",
          borderColor: open ? T.olive : "rgba(239,229,216,0.8)",
          color: selectedOption ? "#1F1F1F" : T.muted,
          boxShadow: open ? "0 4px 20px rgba(107,138,90,0.18)" : "none",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <span className="flex items-center gap-1.5 truncate min-w-0">
          {Icon && <Icon size={14} style={{ color: open ? T.olive : T.muted }} className="flex-shrink-0" />}
          <span className="truncate">{selectedOption ? selectedOption.label : (label || placeholder || "Select...")}</span>
        </span>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex items-center justify-center ml-0.5 flex-shrink-0"
        >
          <FiChevronDown size={14} style={{ color: open ? T.olive : T.muted }} />
        </motion.div>
      </motion.button>

      {open && createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, y: menuPos.isUpward ? 6 : -6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="custom-dropdown-portal-popover rounded-[20px] p-2 shadow-2xl text-left flex flex-col"
            style={{
              position: "fixed",
              top: `${menuPos.top}px`,
              left: `${menuPos.left}px`,
              width: `${menuPos.width}px`,
              zIndex: 99999999,
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(28px)",
              WebkitBackdropFilter: "blur(28px)",
              border: "1px solid rgba(239,229,216,0.95)",
              boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
              maxHeight: "280px",
            }}
          >
            {/* Search Bar */}
            {searchable && (options || []).length > 3 && (
              <div className="p-1 mb-1.5 border-b border-zinc-100/80 sticky top-0 bg-white/90 backdrop-blur-md z-10">
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-zinc-100/90 border border-zinc-200/70 focus-within:border-[#6B8A5A] transition-all">
                  <FiSearch size={13} className="text-zinc-400 shrink-0" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search options..."
                    className="w-full text-[11.5px] bg-transparent outline-none text-zinc-800 placeholder:text-zinc-400 font-medium"
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSearchTerm(""); }}
                      className="text-zinc-400 hover:text-zinc-600 bg-transparent border-none cursor-pointer p-0 flex items-center justify-center"
                    >
                      <FiX size={12} />
                    </button>
                  )}
                </div>
              </div>
            )}

            {label && (
              <div className="px-2 py-1 mb-1 text-[9px] font-black uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
                {label}
              </div>
            )}

            <div className="overflow-y-auto flex-1 max-h-[220px] pr-0.5" style={{ scrollbarWidth: "thin", msOverflowStyle: "none" }}>
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-3 text-center text-[11.5px] text-zinc-400 italic">
                  No matching items found
                </div>
              ) : (
                filteredOptions.map((opt) => {
                  const isSelected = String(opt.value) === String(value);
                  return (
                    <motion.button
                      key={String(opt.value)}
                      type="button"
                      whileHover={{ x: 2, background: "rgba(107,138,90,0.08)" }}
                      onClick={() => {
                        onChange(opt.value);
                        setOpen(false);
                      }}
                      className="w-full flex items-center justify-between px-2.5 py-2 text-[12px] font-semibold rounded-[10px] border-none bg-transparent cursor-pointer transition-all mb-0.5 text-left"
                      style={{
                        color: isSelected ? T.olive : "#333",
                        fontFamily: "'Inter', sans-serif",
                        background: isSelected ? "rgba(107,138,90,0.1)" : "transparent",
                      }}
                    >
                      <span className="flex items-center gap-2 truncate min-w-0">
                        {opt.dotColor && (
                          <span
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ background: opt.dotColor }}
                          />
                        )}
                        <span className="truncate">{opt.label}</span>
                      </span>
                      {isSelected && <FiCheck size={13} style={{ color: T.olive }} className="flex-shrink-0 ml-1" />}
                    </motion.button>
                  );
                })
              )}
            </div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}

function KpiCard({ icon:Icon, value, label, trend, trendUp=true, iconBg, iconColor, accentColor, cardBg, delay=0 }) {
  return (
    <motion.div
      initial={{ opacity:0, y:18 }} animate={{ opacity:1, y:0 }}
      transition={{ delay, duration:0.45, ease:[0.25,1,0.5,1] }}
      whileHover={{ y:-5, boxShadow:"0 24px 50px -12px rgba(0,0,0,0.12)" }}
      className="ad-kpi-card group relative overflow-hidden rounded-[24px] p-5 border transition-all duration-300 flex flex-col justify-between"
      style={{
        background: cardBg || "rgba(255,255,255,0.88)",
        borderColor: "rgba(255,255,255,0.95)",
        backdropFilter: "blur(28px)",
        WebkitBackdropFilter: "blur(28px)",
        boxShadow: "0 10px 30px -5px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(239, 229, 216, 0.5)",
      }}>
      
      {/* Dynamic ambient orb */}
      <div className="ad-kpi-orb absolute -top-10 -right-10 w-28 h-28 rounded-full opacity-25 group-hover:opacity-60 transition-opacity duration-500 pointer-events-none"
        style={{ background: accentColor || iconBg, filter: "blur(28px)" }} />

      <div className="ad-kpi-body">
        <div className="ad-kpi-iconrow flex items-center justify-between mb-3.5">
          <div className="ad-kpi-icon w-11 h-11 rounded-[15px] flex items-center justify-center flex-shrink-0 text-white shadow-sm transition-transform duration-300 group-hover:scale-105"
            style={{
              background: iconBg.startsWith("rgba") ? iconBg : iconBg,
              color: iconBg.startsWith("rgba") ? iconColor : "#fff",
              boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
            }}>
            <Icon size={19} />
          </div>

          <div className="ad-kpi-note flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full border tracking-wide"
            style={{
              background: trendUp ? "rgba(34, 197, 94, 0.09)" : "rgba(226, 75, 74, 0.08)",
              color: trendUp ? T.greenDk : T.red,
              borderColor: trendUp ? "rgba(34, 197, 94, 0.2)" : "rgba(226, 75, 74, 0.2)",
              fontFamily: "'Inter', sans-serif"
            }}>
            <FiTrendingUp size={11} style={{ transform: trendUp ? "none" : "rotate(180deg)" }} />
            <span>{trend}</span>
          </div>
        </div>

        <p className="ad-kpi-lbl text-[10.5px] font-black uppercase tracking-[1px] mb-1 text-zinc-400">
          {label}
        </p>

        <p className="ad-kpi-val font-black text-[#1F1F1F] tracking-tight leading-none text-[30px]"
          style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-1.2px" }}>
          {value}
        </p>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-[3px] opacity-70 group-hover:opacity-100 transition-opacity duration-300 rounded-b-[24px]"
        style={{ background: accentColor }} />
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════════════════ */
export default function AdminDashboard() {
  const navigate = useNavigate();
  const { packages: dynamicPackages, tiers: dynamicTiers } = useDynamicPackages();
  const [toast,     setToast]     = useState(null);
  const [liveCount, setLiveCount] = useState(0);
  const [stats,     setStats]     = useState(null);
  const [sessions,  setSessions]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  
  // Navigation / Modal States
  const [chartTab, setChartTab] = useState("registrations");
  const [revenueInterval, setRevenueInterval] = useState("30d"); // "today" | "3d" | "7d" | "15d" | "30d" // "registrations" | "videos" | "sessions" | "revenue"
  const [activeModal, setActiveModal] = useState(null); // "upload" | "schedule" | "diet" | "notification" | null
  const [activeUserList, setActiveUserList] = useState(null); // "registered" | "checkedin" | "active" | "inactive" | null
  const [hoveredRevPt, setHoveredRevPt] = useState(null); // { date, amount, x, y } | null
  const [hoveredGrowthPt, setHoveredGrowthPt] = useState(null); // { month, val, x, y, isMax } | null
  
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  /* ── Fetch real dashboard data from backend ── */
  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, sessionsRes, analyticsRes] = await Promise.all([
        adminApi.getDashboardStats().catch(() => ({ data: {} })),
        liveSessionAPI.getSessions().catch(() => ({ data: { success: false } })),
        adminApi.getAnalytics("12m").catch(() => null)
      ]);
      const normalised = normaliseStats(statsRes.data || {});
      const analyticsKpi = analyticsRes?.data?.data?.kpi || analyticsRes?.data?.kpi || {};

      // If activeUsers was showing total accounts (e.g. 18), align with dynamic active count (excluding expired)
      if (analyticsKpi.activeUsers !== undefined && analyticsKpi.activeUsers !== null) {
        normalised.activeUsers = analyticsKpi.activeUsers;
      }
      if (analyticsKpi.totalRev !== undefined && analyticsKpi.totalRev !== null) {
        normalised.totalRevenue = analyticsKpi.totalRev;
      }

      setStats(normalised);
      setLiveCount(normalised.activeUsers);

      if (sessionsRes.data?.success) {
        const rawSessions = sessionsRes.data.data || [];
        const todayStr = new Date().toDateString();
        const todaySessions = rawSessions
          .filter(s => {
            const classDate = new Date(s.scheduledAt);
            return classDate.toDateString() === todayStr;
          })
          .map(s => {
            const classDate = new Date(s.scheduledAt);
            return {
              title: s.title,
              time: classDate.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true }),
              instr: s.instructorName || "Instructor TBA",
              count: `${s.enrolledCount || 0}/${s.maxCapacity || 30}`,
              live: s.isLive
            };
          });
        setSessions(todaySessions);
      }
    } catch (err) {
      console.error("[Dashboard] Failed to fetch stats:", err.message);
      setError(err.message);
      showToast(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  /* ── Live counter flutter (±1 every 4 s) ── */
  useEffect(() => {
    const iv = setInterval(() =>
      setLiveCount(p => Math.max(0, p + Math.floor(Math.random() * 3) - 1)), 4000);
    return () => clearInterval(iv);
  }, []);

  /* ── Revenue helpers ── */
  const fmtRev = (n) => {
    if (!n || n === 0) return "₹0";
    if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
    if (n >= 1000)   return `₹${(n / 1000).toFixed(0)}K`;
    return `₹${n}`;
  };

  /* ── Revenue bar chart from real revenueByMonth ── */
  const revMonths  = stats?.revenueByMonth || [];
  const MONTHS     = revMonths.length
    ? revMonths.map(r => r.month.split(" ")[0])
    : ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
  const maxRev     = Math.max(...revMonths.map(r => r.revenue), 1);
  const barHeights = revMonths.length
    ? revMonths.map(r => Math.max(12, Math.round((r.revenue / maxRev) * 88)))
    : [12, 12, 12, 12, 12, 12];

  /* ── KPI display values from real data ── */
  const ph = loading ? "…" : "0";                                   // placeholder while loading
  const kpiTotal  = stats ? (stats.totalUsers ?? 0).toLocaleString() : ph;
  const kpiActive = stats ? (stats.activeUsers ?? 0).toLocaleString() : ph;
  const kpiPaid   = stats ? (stats.activeSubscriptions ?? 0).toLocaleString() : ph;
  const kpiRev    = stats ? fmtRev(stats.totalRevenue)                 : ph;
  const kpiPend   = stats ? String(Math.max(0, stats.totalSubscriptions - stats.activeSubscriptions)) : ph;
  const newUsers  = stats ? `+${stats.newUsersThisMonth} this month`   : "this month";

  /* ── Dynamic Priority Alerts (from real database) ── */
  const pa = stats?.priorityAlerts || {};
  const expiringCount   = pa.expiringSoon   ?? pa.ExpiringSoon   ?? 0;
  const inactiveCount   = pa.inactiveUsers  ?? pa.InactiveUsers  ?? (stats?.inactiveUsers || 0);
  const failedCount     = pa.failedPayments ?? pa.FailedPayments ?? 0;
  const completionCount = pa.nearProgramEnd ?? pa.NearProgramEnd ?? 0;
  const newCount        = pa.newThisMonth   ?? pa.NewThisMonth   ?? (stats?.newUsersThisMonth || 0);
  const healthCount     = pa.healthFlagged  ?? pa.HealthFlagged  ?? 0;
  const totalAlerts     = pa.total ?? pa.Total ?? (expiringCount + inactiveCount + failedCount + completionCount + newCount + healthCount);

  /* ── Average Streak calculation ── */
  const avgStreak = useMemo(() => {
    const list = stats?.usersList || [];
    if (list.length === 0) return "0.0";
    const totalStreak = list.reduce((acc, u) => acc + (u.streak || 0), 0);
    return (totalStreak / list.length).toFixed(1);
  }, [stats]);

  const alerts = [
    { 
      id: "expiring",
      icon: FiCalendar,   
      count: expiringCount,
      title: "Expiring soon",        
      sub: "Subscriptions ending in 7 days", 
      color: T.red,     
      bg: "rgba(226,75,74,0.1)",
      route: "/admin/subscriptions?tab=all&status=expiring"
    },
    { 
      id: "inactive",
      icon: FiUserX,      
      count: inactiveCount,
      title: "Inactive users", 
      sub: "No login in 14+ days", 
      color: "#F59E0B", 
      bg: "rgba(245,158,11,0.1)",
      route: "/admin/high-priority-users?tab=inactive"
    },
    { 
      id: "failed",
      icon: FiCreditCard, 
      count: failedCount,
      title: "Failed payments",       
      sub: "Retry or contact required",       
      color: T.red,     
      bg: "rgba(226,75,74,0.1)",
      route: "/admin/subscriptions?tab=pending"
    },
    { 
      id: "completion",
      icon: FiFrown,      
      count: completionCount,
      title: "Near program end",      
      sub: "90%+ program completion",          
      color: T.greenDk, 
      bg: "rgba(34,197,94,0.1)",
      route: "/admin/high-priority-users?tab=completion"
    },
    { 
      id: "new",
      icon: FiUserPlus,   
      count: newCount,
      title: "Onboarding Pending", 
      sub: "New members awaiting setup", 
      color: T.olive, 
      bg: "rgba(107,138,90,0.1)",
      route: "/admin/users?filter=new"
    },
    { 
      id: "health",
      icon: FiHeart,      
      count: healthCount,
      title: "Health flagged",        
      sub: "Instructor review needed",        
      color: T.red,     
      bg: "rgba(226,75,74,0.08)",
      route: "/admin/high-priority-users?tab=health"
    },
  ];


  if (loading && !stats) {
    return (
      <AdminDashboardLayout activeNav="dashboard">
        <BrandLoader 
          message="Loading Admin Command Center..." 
          subMessage="Aggregating platform metrics, users, live classes & analytics" 
        />
      </AdminDashboardLayout>
    );
  }

  return (
    <div style={{ fontFamily:"'Inter',sans-serif", background:"#FFF8EE", minHeight:"100vh" }}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@700;800;900&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <AdminDashboardLayout activeNav="dashboard">

        {/* Ambient orbs */}
        <motion.div animate={{ y:[0,-20,0], x:[0,14,0] }} transition={{ duration:14, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(242,154,12,0.07),transparent 70%)", filter:"blur(80px)", top:-160, right:-100, zIndex:0 }} />
        <motion.div animate={{ y:[0,16,0], x:[0,-10,0] }} transition={{ duration:17, repeat:Infinity, ease:"easeInOut" }}
          className="fixed w-[400px] h-[400px] rounded-full pointer-events-none"
          style={{ background:"radial-gradient(circle,rgba(107,138,90,0.06),transparent 70%)", filter:"blur(80px)", bottom:-100, left:0, zIndex:0 }} />

        <div className="relative z-10 w-full min-w-0">



          {/* ── Error banner (only when API call fails) ── */}
          {error && !loading && (
            <div className="mb-4 px-4 py-3 rounded-[14px] text-[13px] font-semibold flex items-center gap-2"
              style={{ background:"rgba(226,75,74,0.08)", border:"1px solid rgba(226,75,74,0.25)", color:T.red }}>
              <FiAlertTriangle size={15} />
              {error}. Check the browser console for details.
              <button onClick={fetchStats} className="ml-auto underline cursor-pointer text-[12px]" style={{ background:"none", border:"none", color:T.red }}>
                Retry
              </button>
            </div>
          )}


          {/* ── KPI TILES   all real & unique data ── */}
          <div className="ad-kpi grid grid-cols-4 gap-4 mb-5">
            <div className="cursor-pointer" onClick={() => setActiveUserList("registered")}>
              <KpiCard
                icon={FiUsers}          value={kpiTotal}  label="Registered Users"
                trend={newUsers}        trendUp
                iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`} iconColor={T.orange}
                accentColor={`linear-gradient(90deg,${T.orange},${T.orange2})`}
                cardBg="linear-gradient(145deg,#FFF4E8,#FFF8EE)" delay={0.06} />
            </div>
            <div className="cursor-pointer" onClick={() => setActiveUserList("active")}>
              <KpiCard
                icon={FiUserPlus}       value={kpiActive} label="Active Users"
                trend="currently active" trendUp
                iconBg="rgba(34,197,94,0.15)" iconColor={T.greenDk} accentColor={T.green} delay={0.1} />
            </div>
            <KpiCard
              icon={FiTrendingUp}     value={stats ? `${avgStreak}d` : ph} label="Avg Member Streak"
              trend="practice consistency" trendUp
              iconBg="rgba(245,158,11,0.15)" iconColor={T.orange} accentColor={T.orange} delay={0.12} />
            <KpiCard
              icon={FiFeather}        value={kpiPaid}   label="Paid Subscribers"
              trend="active subscriptions" trendUp
              iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`} iconColor={T.olive}
              accentColor={`linear-gradient(90deg,${T.olive},${T.olive2})`}
              cardBg="linear-gradient(145deg,#F5FAF2,#F9FDF5)" delay={0.14} />
            <KpiCard
              icon={FiCreditCard}     value={stats ? fmtRev(stats?.totalRevenue ?? 0) : ph} label="Total Revenue"
              trend="all time total" trendUp
              iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`} iconColor={T.orange}
              accentColor={`linear-gradient(90deg,${T.orange},${T.orange2})`} delay={0.18} />
            <KpiCard
              icon={FiVideo}          value={stats ? (stats.totalPracticeVideos ?? 0).toLocaleString() : ph} label="Total Videos"
              trend="practice library" trendUp
              iconBg="rgba(107,138,90,0.15)" iconColor={T.olive} accentColor={T.olive} delay={0.2} />
            <KpiCard
              icon={FiCalendar}       value={stats ? (stats.totalLiveSessions ?? 0).toLocaleString() : ph} label="Live Sessions"
              trend="conducted/scheduled" trendUp
              iconBg="rgba(242,154,12,0.15)" iconColor={T.orange} accentColor={T.orange} delay={0.22} />
            <div className="cursor-pointer" onClick={() => setActiveUserList("checkedin")}>
              <KpiCard
                icon={FiCheck}          value={stats ? (stats.todayCheckedInCount ?? 0).toLocaleString() : ph} label="Checked In Today"
                trend="attendance log" trendUp
                iconBg="rgba(34,197,94,0.15)" iconColor={T.greenDk} accentColor={T.green} delay={0.24} />
            </div>
          </div>

          {/* ── CHARTS ROW ── */}
          <div className="ad-1col grid grid-cols-2 gap-4 mb-4">

            {/* Dynamic Growth Chart with Full Y-Axis & X-Axis */}
            <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.1 }}
              className="ad-card rounded-[28px] p-6" style={glass}>
              <div className="flex flex-col gap-3 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-black text-[16px] text-[#1F1F1F]"
                      style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.4px" }}>Growth Analytics</h2>
                    <p className="text-[11px] mt-0.5" style={{ color:T.muted }}>Monthly trends.</p>
                  </div>
                  <span className="text-[11px] font-bold text-[#6B8A5A] bg-[#6B8A5A]/10 px-2.5 py-1 rounded-full capitalize">
                    {chartTab} Trend
                  </span>
                </div>
                
                {/* Metric Tab Selector Buttons */}
                <div className="flex gap-1.5 flex-wrap">
                  {[
                    { id: "registrations", label: "Registrations" },
                    { id: "videos",        label: "Videos Uploads" },
                    { id: "sessions",      label: "Live Sessions" },
                    { id: "revenue",       label: "Revenue" }
                  ].map(tab => (
                    <button key={tab.id} onClick={() => setChartTab(tab.id)}
                      className="h-7 px-3 rounded-full text-[10px] font-semibold transition-all border outline-none cursor-pointer"
                      style={chartTab === tab.id
                        ? { background: "rgba(107,138,90,0.12)", borderColor: "rgba(107,138,90,0.35)", color: T.olive }
                        : { background: "rgba(255,255,255,0.85)", borderColor: T.border, color: T.muted }
                      }>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Chart Body with Y-Axis and SVG Graph */}
              {(() => {
                const data = stats ? (
                  chartTab === "revenue" ? stats.revenueByMonth.map(r => r.revenue) :
                  chartTab === "videos" ? stats.videosByMonth.map(r => r.count) :
                  chartTab === "sessions" ? stats.sessionsByMonth.map(r => r.count) :
                  stats.registrationsByMonth.map(r => r.count)
                ) : [0, 0, 0, 0, 0, 0];

                const rawMax = Math.max(...data, 1);
                
                const formatYLabel = (val) => {
                  if (val === 0) return "0";
                  if (chartTab === "revenue") {
                    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
                    if (val >= 1000) return `₹${(val / 1000).toFixed(0)}k`;
                    return `₹${Math.round(val)}`;
                  }
                  if (val >= 1000) return `${(val / 1000).toFixed(1)}k`;
                  return String(Math.round(val));
                };

                const formatTooltipVal = (val) => {
                  if (chartTab === "revenue") {
                    return `₹${Number(val || 0).toLocaleString()}`;
                  }
                  if (chartTab === "registrations") {
                    return `${Number(val || 0).toLocaleString()} Users`;
                  }
                  if (chartTab === "videos") {
                    return `${Number(val || 0).toLocaleString()} Videos`;
                  }
                  if (chartTab === "sessions") {
                    return `${Number(val || 0).toLocaleString()} Sessions`;
                  }
                  return Number(val || 0).toLocaleString();
                };

                const yTicks = [
                  { label: formatYLabel(rawMax), pos: 10 },
                  { label: formatYLabel(rawMax * 0.66), pos: 45 },
                  { label: formatYLabel(rawMax * 0.33), pos: 80 },
                  { label: "0", pos: 112 }
                ];

                const strokeColor = chartTab === "revenue" || chartTab === "videos" ? "#F29A0C" : "#6B8A5A";
                const svgW = 460;
                const svgH = 120;
                const padTop = 10;
                const padBottom = 15;
                const plotH = svgH - padTop - padBottom;

                const pts = data.map((v, i) => {
                  const x = data.length > 1 ? (i / (data.length - 1)) * svgW : svgW / 2;
                  const y = padTop + plotH - (v / rawMax) * plotH;
                  return { x, y, val: v, month: MONTHS[i] || `M${i+1}` };
                });

                let pathD = pts.length > 0 ? `M${pts[0].x},${pts[0].y}` : "";
                for (let i = 1; i < pts.length; i++) {
                  const prev = pts[i - 1];
                  const curr = pts[i];
                  const cx = (prev.x + curr.x) / 2;
                  pathD += ` C${cx},${prev.y} ${cx},${curr.y} ${curr.x},${curr.y}`;
                }
                const fillD = pts.length > 0 ? `${pathD} L${pts[pts.length - 1].x},${svgH} L${pts[0].x},${svgH} Z` : "";

                return (
                  <div>
                    {/* Y-Axis + Graph Row */}
                    <div className="flex gap-2 mb-1.5">
                      {/* Dynamic Y-Axis Column */}
                      <div className="flex flex-col justify-between text-[10px] font-bold text-zinc-400 text-right w-11 h-[120px] select-none py-0.5 border-r border-zinc-200/80 pr-1.5 shrink-0">
                        {yTicks.map((tick, idx) => (
                          <span key={idx} className="leading-none">{tick.label}</span>
                        ))}
                      </div>

                      {/* SVG Canvas Area with Interactive Hover */}
                      <div className="flex-1 relative h-[120px] overflow-visible" onMouseLeave={() => setHoveredGrowthPt(null)}>
                        <svg width="100%" height="100%" viewBox={`0 0 ${svgW} ${svgH}`} preserveAspectRatio="none" className="overflow-visible w-full h-full">
                          <defs>
                            <linearGradient id="admFillDynamic" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor={strokeColor} stopOpacity=".22"/>
                              <stop offset="100%" stopColor={strokeColor} stopOpacity="0"/>
                            </linearGradient>
                          </defs>

                          {/* Grid Guidelines aligned with Y-axis */}
                          {[10, 45, 80, 112].map(y => (
                            <line key={y} x1="0" y1={y} x2={svgW} y2={y} stroke="rgba(239,229,216,0.7)" strokeDasharray="3 3" strokeWidth=".8"/>
                          ))}

                          {/* Area & Smooth Line */}
                          {fillD && <path fill="url(#admFillDynamic)" d={fillD}/>}
                          {pathD && <path fill="none" stroke={strokeColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" d={pathD}/>}

                          {/* Hover Vertical Guide Line */}
                          {hoveredGrowthPt && (
                            <line
                              x1={hoveredGrowthPt.x}
                              y1={5}
                              x2={hoveredGrowthPt.x}
                              y2={svgH - 5}
                              stroke={strokeColor}
                              strokeWidth="1.5"
                              strokeDasharray="3 2"
                              opacity="0.85"
                            />
                          )}

                          {/* Interactive Data Point Markers & Column Hit-Zones */}
                          {pts.map((p, idx) => {
                            const isHovered = hoveredGrowthPt && hoveredGrowthPt.month === p.month;
                            const isMax = p.val === rawMax && rawMax > 0;
                            const colW = svgW / Math.max(1, pts.length);
                            return (
                              <g
                                key={idx}
                                className="cursor-pointer"
                                style={{ pointerEvents: "all", cursor: "pointer" }}
                                onMouseEnter={() => setHoveredGrowthPt({ month: p.month, val: p.val, x: p.x, y: p.y, isMax })}
                                onMouseMove={() => setHoveredGrowthPt({ month: p.month, val: p.val, x: p.x, y: p.y, isMax })}>
                                <title>{`${p.month}: ${formatTooltipVal(p.val)}`}</title>

                                {/* Full-Height Column Hit Box */}
                                <rect
                                  x={Math.max(0, p.x - colW / 2)}
                                  y={0}
                                  width={colW}
                                  height={svgH}
                                  fill="rgba(0,0,0,0.0001)"
                                  style={{ pointerEvents: "all", cursor: "pointer" }}
                                />

                                {/* Wide circular hit target */}
                                <circle cx={p.x} cy={p.y} r="22" fill="rgba(0,0,0,0.0001)" style={{ pointerEvents: "all" }} />

                                {/* Glowing aura on hover or peak */}
                                {(isHovered || isMax) && (
                                  <circle
                                    cx={p.x}
                                    cy={p.y}
                                    r={isHovered ? "11" : "7"}
                                    fill={strokeColor}
                                    opacity={isHovered ? "0.38" : "0.15"}
                                    className="transition-all"
                                  />
                                )}

                                {/* Main Data Dot */}
                                <circle
                                  cx={p.x}
                                  cy={p.y}
                                  r={isHovered ? 6 : 3.8}
                                  fill="#fff"
                                  stroke={strokeColor}
                                  strokeWidth={isHovered ? "2.5" : "2"}
                                  className="transition-all"
                                />
                              </g>
                            );
                          })}
                        </svg>

                        {/* Floating Tooltip Bubble when Hovered */}
                        {hoveredGrowthPt && (
                          <div
                            className="absolute pointer-events-none z-50 px-3 py-1.5 rounded-xl shadow-2xl border border-zinc-700/50 text-center backdrop-blur-md"
                            style={{
                              background: "rgba(24, 24, 27, 0.95)",
                              left: `${(hoveredGrowthPt.x / svgW) * 100}%`,
                              top: `${(hoveredGrowthPt.y / svgH) * 100}%`,
                              transform: hoveredGrowthPt.y < 45 ? "translate(-50%, 12px)" : "translate(-50%, calc(-100% - 12px))",
                              minWidth: "90px",
                              boxShadow: "0 12px 30px rgba(0,0,0,0.3)"
                            }}>
                            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider leading-tight">
                              {hoveredGrowthPt.month}
                            </p>
                            <p className="text-[13px] font-black leading-tight mt-0.5" style={{ color: strokeColor === "#6B8A5A" ? "#4ade80" : "#fbbf24" }}>
                              {formatTooltipVal(hoveredGrowthPt.val)}
                            </p>
                            {hoveredGrowthPt.isMax && (
                              <span className="text-[8.5px] font-extrabold text-amber-400 uppercase tracking-wide block mt-0.5">
                                ★ Peak Month
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* X-Axis Month Labels (offset to align with graph canvas) */}
                    <div className="flex justify-between pl-13 pr-1 mb-3 text-[10px] font-semibold text-zinc-500">
                      {MONTHS.map((m, i) => (
                        <span key={`${m}-${i}`} style={{ color: i === MONTHS.length - 1 ? strokeColor : undefined }} className={i === MONTHS.length - 1 ? "font-bold" : ""}>
                          {m}
                        </span>
                      ))}
                    </div>

                    {/* Legend & Hover Data Status */}
                    <div className="flex items-center justify-between pt-2 border-t border-zinc-100/90 text-[11px] text-zinc-500 font-medium">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5">
                          <div className="w-3.5 h-[3px] rounded-full" style={{ background: strokeColor }}/>
                          <span className="capitalize">{chartTab} Trend</span>
                        </div>
                        {hoveredGrowthPt && (
                          <div className="flex items-center gap-1.5 font-bold" style={{ color: strokeColor }}>
                            <span className="w-1.5 h-1.5 rounded-full inline-block animate-pulse" style={{ background: strokeColor }} />
                            <span>{hoveredGrowthPt.month}: {formatTooltipVal(hoveredGrowthPt.val)}</span>
                          </div>
                        )}
                      </div>
                      <span className="text-[10px] font-bold text-zinc-400">
                        Peak: {formatYLabel(rawMax)}
                      </span>
                    </div>
                  </div>
                );
              })()}
            </motion.div>

            {/* Subscription Mix */}
            <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.12 }}
              className="ad-card rounded-[28px] p-6" style={glass}>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="font-black text-[16px] text-[#1F1F1F]"
                    style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.4px" }}>Subscription Mix</h2>
                  <p className="text-[11px] mt-1" style={{ color:T.muted }}>
                    {stats?.activeSubscriptions || 0} active paid subscribers ({stats?.totalUsers > 0 ? Math.round(((stats.activeSubscriptions || 0) / stats.totalUsers) * 100) : 0}% of platform)
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-6">
                {(() => {
                  const mixObj = stats?.subscriptionMix || {};
                  const totalUsers = stats?.totalUsers || 1;
                  const paidCount = stats?.activeSubscriptions || 0;

                  // Color palette for tier access names
                  const getTierColor = (name, i) => {
                    const low = (name || "").toLowerCase();
                    if (low.includes("free") || low.includes("trial")) return "#A1A1AA";
                    if (low.includes("prem")) return "#F29A0C";
                    if (low.includes("stand")) return "#6B8A5A";
                    if (low.includes("basic")) return "#185FA5";
                    if (low.includes("pro") || low.includes("ytt")) return "#534AB7";
                    if (low.includes("gold")) return "#EAB308";
                    if (low.includes("vip")) return "#EC4899";
                    const colors = ["#0D9488", "#6366F1", "#F97316", "#84CC16", "#06B6D4", "#E11D48"];
                    return colors[i % colors.length];
                  };

                  const activeTierNames = (dynamicTiers || []).map(t => {
                    const name = typeof t === "string" ? t : (t.name || t.packageType || String(t));
                    return name ? name.trim().toLowerCase() : "";
                  }).filter(Boolean);

                  const activeTierSet = new Set(activeTierNames);

                  const seen = new Set();
                  const items = [];

                  // Populate active tiers dynamically from dynamicTiers and mixObj
                  (dynamicTiers || []).forEach(t => {
                    const rawName = typeof t === "string" ? t : (t.name || t.packageType || String(t));
                    if (!rawName) return;
                    const normKey = packageService.normalizeTierName(rawName);
                    if (normKey.toLowerCase() === "free trial" || normKey.toLowerCase() === "free") return;
                    if (!seen.has(normKey.toLowerCase())) {
                      seen.add(normKey.toLowerCase());
                      let count = mixObj[normKey] ?? mixObj[rawName] ?? 0;
                      if (count === 0) {
                        const matchingKey = Object.keys(mixObj).find(k => k.trim().toLowerCase() === normKey.toLowerCase());
                        if (matchingKey) count = mixObj[matchingKey] || 0;
                      }
                      items.push({
                        key: normKey,
                        label: normKey,
                        count: count,
                        isFree: false
                      });
                    }
                  });

                  // Include Free Trial if present in mixObj or if no packages are configured
                  const freeTrialCount = mixObj["Free Trial"] ?? mixObj["free trial"] ?? mixObj["Free"] ?? 0;
                  if (!seen.has("free trial")) {
                    items.push({
                      key: "Free Trial",
                      label: "Free Trial",
                      count: freeTrialCount,
                      isFree: true
                    });
                    seen.add("free trial");
                  }

                  // Also include any active tiers in mixObj not yet covered
                  Object.keys(mixObj).forEach(k => {
                    const normKey = packageService.normalizeTierName(k);
                    if (!seen.has(normKey.toLowerCase()) && (activeTierSet.size === 0 || activeTierSet.has(normKey.toLowerCase()))) {
                      seen.add(normKey.toLowerCase());
                      items.push({
                        key: normKey,
                        label: normKey,
                        count: mixObj[k] || 0,
                        isFree: normKey.toLowerCase().includes("free") || normKey.toLowerCase().includes("trial")
                      });
                    }
                  });

                  // Calculate total accounted active users
                  const totalAccounted = items.reduce((sum, it) => sum + (it.count || 0), 0);
                  const baseForPct = totalAccounted > 0 ? totalAccounted : (totalUsers > 0 ? totalUsers : 1);

                  // Sort: Paid tiers with highest counts first, then Free Trial
                  items.sort((a, b) => {
                    if (a.isFree !== b.isFree) return a.isFree ? 1 : -1;
                    if (b.count !== a.count) return b.count - a.count;
                    return a.label.localeCompare(b.label);
                  });

                  // Calculate multi-segment slices for the 3D donut visualization (100% total circumference)
                  const R = 50;
                  const C = 2 * Math.PI * R; // ~314.16
                  let runningOffset = 0;
                  const slices = items.map((item, idx) => {
                    const pct = baseForPct > 0 ? (item.count / baseForPct) : 0;
                    const len = C * pct;
                    const slice = {
                      ...item,
                      color: getTierColor(item.label, idx),
                      len,
                      offset: runningOffset,
                      pct: Math.round(pct * 100)
                    };
                    runningOffset += len;
                    return slice;
                  });

                  return (
                    <>
                      {/* Dynamic Multi-Segment Donut Chart (100% Complete Gapless Circle) */}
                      <div style={{ position:"relative", width:130, height:130, flexShrink:0 }}>
                        <svg viewBox="0 0 130 130" style={{ position:"absolute", inset:0, width:"100%", height:"100%", transform:"rotate(-90deg)", filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.08))" }}>
                          <circle cx="65" cy="65" r={R} fill="none" stroke="rgba(239,229,216,0.3)" strokeWidth="15"/>
                          {slices.filter(s => s.len > 0).map((s) => (
                            <motion.circle
                              key={s.key}
                              cx="65"
                              cy="65"
                              r={R}
                              fill="none"
                              stroke={s.color}
                              strokeWidth="15"
                              strokeDasharray={`${s.len} ${Math.max(0, C - s.len)}`}
                              strokeDashoffset={-s.offset}
                              strokeLinecap="butt"
                              initial={{ strokeDasharray: `0 ${C}` }}
                              animate={{ strokeDasharray: `${s.len} ${Math.max(0, C - s.len)}` }}
                              transition={{ duration: 0.8, ease: "easeOut" }}
                            />
                          ))}
                        </svg>
                        <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                          <p className="font-black text-[#1F1F1F]"
                            style={{ fontFamily:"'Poppins',sans-serif", fontSize:20, letterSpacing:"-0.8px", lineHeight:1 }}>
                            {paidCount}
                          </p>
                          <p className="text-[9.5px] font-bold uppercase tracking-wider text-zinc-500 mt-0.5">PAID USERS</p>
                          <p className="text-[8.5px] text-zinc-400 font-semibold mt-0.5">OF {totalUsers} TOTAL</p>
                        </div>
                      </div>

                      {/* Dynamic Tier Access Names List */}
                      <div className="flex-1 flex flex-col gap-2 max-h-[175px] overflow-y-auto pr-1">
                        {slices.map(item => (
                          <div key={item.key} className="flex items-center justify-between gap-3 text-[12px]">
                            <div className="flex items-center gap-2 truncate">
                              <div className="w-2.5 h-2.5 rounded-[3px] flex-shrink-0" style={{ background: item.color }} />
                              <span className="font-semibold text-zinc-700 truncate">{item.label} {item.label.toLowerCase().includes("registered") ? "" : "Tier"}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${item.count > 0 ? "bg-amber-100/70 text-amber-900" : "bg-zinc-100 text-zinc-400"}`}>
                                {item.pct}%
                              </span>
                              <span className={`font-black w-6 text-right ${item.count > 0 ? "text-zinc-900" : "text-zinc-400"}`}>
                                {item.count}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            </motion.div>
          </div>

          {/* ── ALERTS ── */}
          <div className="ad-1col grid grid-cols-[1fr_320px] gap-4 mb-4">

            {/* Operational & System Overview (100% unique system metrics) */}
            <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.16 }}
              className="ad-card rounded-[28px] p-6" style={glass}>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="font-black text-[16px] text-[#1F1F1F]"
                    style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.4px" }}>
                    Platform Operational Overview
                  </h2>
                  <p className="text-[11px] mt-1" style={{ color:T.muted }}>System architecture & administrative status</p>
                </div>
                <span className="text-[10px] font-black text-emerald-800 bg-emerald-100/90 px-2.5 py-1 rounded-full border border-emerald-300">
                  SYSTEM ONLINE
                </span>
              </div>
              <div className="ad-2col grid grid-cols-3 gap-4">
                {[
                  { label:"System Admins",       value:stats ? (stats.totalAdmins ?? 0).toLocaleString() : ph, sub:"SuperAdmin Accounts", icon:FiShield,     color:"#534AB7", bg:"rgba(83,74,183,0.08)" },
                  { label:"Membership Plans",   value:stats ? (stats.totalPackages ?? 0).toLocaleString() : ph, sub:"Configured Plans", icon:FiFeather,  color:T.olive,  bg:"rgba(107,138,90,0.08)" },
                  { label:"Total Subscriptions",value:stats ? (stats.totalSubscriptions ?? 0).toLocaleString() : ph, sub:"All Time Enrolments", icon:FiCreditCard, color:T.orange, bg:"rgba(242,154,12,0.08)" },
                  { label:"Class Density",       value:stats ? (stats.totalLiveSessions > 0 ? ((stats.todayCheckedInCount ?? 0) / stats.totalLiveSessions).toFixed(1) : "0.0") : ph, sub:"Avg per live session", icon:FiActivity, color:"#2563EB", bg:"rgba(239,246,255,0.8)" },
                  { label:"Active Paid Plans",   value:stats ? (stats.activeSubscriptions ?? 0).toLocaleString() : ph, sub:"Current Paid Users", icon:FiCheck, color:T.greenDk, bg:"rgba(34,197,94,0.08)" },
                  { 
                    label:"Today's Check-Ins",   
                    value:stats ? (stats.todayCheckedInCount ?? 0).toLocaleString() : ph, 
                    sub: stats ? `${stats.currentlyCheckedInCount ?? 0} in session · ${stats.todayCheckedOutCount ?? 0} out` : "Live Attendance Logs", 
                    icon:FiTrendingUp,
                    color:T.greenDk,
                    bg:"rgba(34,197,94,0.08)",
                    onClick: () => setActiveUserList("checkedin")
                  },
                  { label:"Diet Plans Catalog", value:stats ? (stats.totalDietPlans ?? 0).toLocaleString() : ph, sub:"Published Meal Guides", icon:FiAnchor, color:"#8B5CF6", bg:"rgba(139,92,246,0.08)" },
                  { label:"Online Right Now",   value:stats ? (stats.onlineUsersCount ?? 0).toLocaleString() : ph, sub:"Active User Sessions", icon:FiActivity, color:T.greenDk, bg:"rgba(34,197,94,0.08)" },
                  { label:"Dormant Accounts",   value:stats ? (stats.deactivatedUsers ?? 0).toLocaleString() : ph, sub:"Flagged / Inactive", icon:FiUserX, color:T.red, bg:"rgba(226,75,74,0.08)" },
                ].map(item => (
                  <div key={item.label} 
                    onClick={item.onClick}
                    className={`rounded-[18px] p-4 flex flex-col justify-between transition-all ${item.onClick ? "cursor-pointer hover:shadow-md hover:scale-[1.01]" : ""}`}
                    style={{ background:item.bg, border:"1px solid rgba(255,255,255,0.7)" }}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="w-8 h-8 rounded-[10px] flex items-center justify-center"
                        style={{ background:"rgba(255,255,255,0.85)", boxShadow:"0 2px 8px rgba(0,0,0,0.04)" }}>
                        <item.icon size={15} style={{ color:item.color }}/>
                      </div>
                      <span className="text-[9.5px] font-bold text-zinc-400 uppercase tracking-wider">{item.sub}</span>
                    </div>
                    <div>
                      <p className="font-black text-[#1F1F1F]"
                        style={{ fontFamily:"'Poppins',sans-serif", fontSize:20, letterSpacing:"-0.8px", lineHeight:1 }}>
                        {item.value}
                      </p>
                      <p className="text-[10px] font-bold uppercase tracking-[0.6px] mt-1" style={{ color:T.muted }}>
                        {item.label}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Priority Alerts (Fully Dynamic with Real-time DB Counts) */}
            <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.18 }}
              className="ad-card rounded-[28px] p-5" style={glass}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="font-black text-[16px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>
                    Priority Alerts
                  </h2>
                  <p className="text-[11px] mt-0.5" style={{ color:T.muted }}>Requires admin attention</p>
                </div>
                <span className="text-[11px] font-black px-2.5 py-1 rounded-full flex items-center gap-1"
                  style={{ background:"rgba(226,75,74,0.1)", color:T.red, border:"1px solid rgba(226,75,74,0.2)" }}>
                  {totalAlerts} URGENT
                </span>
              </div>
              {alerts.map((a,i) => (
                <motion.div key={a.id || i}
                  whileHover={{ x:4, background:"rgba(255,255,255,0.95)", boxShadow:"0 6px 20px rgba(0,0,0,0.06)" }}
                  onClick={() => {
                    if (a.id === "inactive") {
                      setActiveUserList("inactive");
                    } else if (a.route) {
                      navigate(a.route);
                    } else {
                      showToast(`Opening: ${a.title}`);
                    }
                  }}
                  className="flex items-center gap-2.5 p-3 rounded-[14px] mb-2 cursor-pointer border border-transparent hover:border-amber-200 transition-all duration-200"
                  transition={{ duration:0.22 }}>
                  <div className="w-9 h-9 rounded-[11px] flex items-center justify-center flex-shrink-0"
                    style={{ background:a.bg }}>
                    <a.icon size={15} style={{ color:a.color }}/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[13px] font-bold text-[#1F1F1F] truncate">{a.title}</p>
                      <span className="text-[10px] font-black px-2 py-0.5 rounded-full flex-shrink-0"
                        style={{ background: a.count > 0 ? `${a.color}18` : "rgba(0,0,0,0.05)", color: a.count > 0 ? a.color : "#999", border: `1px solid ${a.count > 0 ? a.color + "30" : "transparent"}` }}>
                        {a.count}
                      </span>
                    </div>
                    <p className="text-[11px] mt-0.5 truncate" style={{ color:T.muted }}>{a.sub}</p>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="flex-shrink-0">
                    <path d="M5 3l4 4-4 4" stroke={T.light} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* ── BOTTOM ROW ── */}
          <div className="ad-1col grid grid-cols-3 gap-4">

            {/* Dynamic Revenue Analytics Multi-Interval Widget with Full-Height SVG, Y-Axis & X-Axis */}
            <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.2 }}
              className="ad-card rounded-[28px] p-6 flex flex-col justify-between" style={{ ...glass, borderRadius:28 }}>
              
              {/* Header & Interval Tabs */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h2 className="font-black text-[16px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.3px" }}>
                      Revenue Analytics
                    </h2>
                    <p className="text-[11px] mt-0.5" style={{ color:T.muted }}>Real Transactions</p>
                  </div>
                  
                  {/* Current Active Interval Amount */}
                  <div className="text-right">
                    <p className="font-black text-[20px]"
                      style={{ fontFamily:"'Poppins',sans-serif", color:T.greenDk, letterSpacing:"-0.5px", lineHeight:1 }}>
                      {(() => {
                        const rb = stats?.revenueBreakdown;
                        if (!rb) return kpiRev;
                        if (revenueInterval === "today") return fmtRev(rb.today);
                        if (revenueInterval === "3d")    return fmtRev(rb.threeDays);
                        if (revenueInterval === "7d")    return fmtRev(rb.oneWeek);
                        if (revenueInterval === "15d")   return fmtRev(rb.fifteenDays);
                        return fmtRev(rb.oneMonth || stats?.totalRevenue);
                      })()}
                    </p>
                    <span className="text-[9.5px] font-bold text-zinc-400 uppercase tracking-wider block mt-1">Interval Total</span>
                  </div>
                </div>

                {/* Interval Pill Selectors */}
                <div className="flex items-center gap-1 p-1 rounded-xl bg-zinc-100/90 mb-4">
                  {[
                    { id: "today", label: "Today" },
                    { id: "3d",    label: "3 Days" },
                    { id: "7d",    label: "1 Week" },
                    { id: "15d",   label: "15 Days" },
                    { id: "30d",   label: "1 Month" },
                  ].map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setRevenueInterval(tab.id)}
                      className={`flex-1 py-1.5 text-[10.5px] font-black rounded-lg transition-all border-none cursor-pointer ${
                        revenueInterval === tab.id
                          ? "bg-white text-emerald-800 shadow-sm"
                          : "bg-transparent text-zinc-500 hover:text-zinc-800"
                      }`}>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Full-Height Graphical Representation with Dynamic Y-Axis & X-Axis */}
              {(() => {
                const dailyPoints = stats?.revenueBreakdown?.dailyPoints || [];
                let pointsToShow = [];
                if (revenueInterval === "today") {
                  pointsToShow = dailyPoints.slice(-1);
                  if (pointsToShow.length === 0) pointsToShow = [{ date: "Today", amount: stats?.revenueBreakdown?.today || 0 }];
                } else if (revenueInterval === "3d") {
                  pointsToShow = dailyPoints.slice(-3);
                } else if (revenueInterval === "7d") {
                  pointsToShow = dailyPoints.slice(-7);
                } else if (revenueInterval === "15d") {
                  pointsToShow = dailyPoints.slice(-15);
                } else {
                  pointsToShow = dailyPoints.slice(-30);
                }

                if (pointsToShow.length === 0) {
                  pointsToShow = [
                    { date: "Day 1", amount: 0 },
                    { date: "Day 2", amount: 0 },
                    { date: "Day 3", amount: 0 }
                  ];
                }

                const rawMax = Math.max(...pointsToShow.map(p => p.amount), 1);
                
                const formatRevY = (val) => {
                  if (val === 0) return "₹0";
                  if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
                  if (val >= 1000) return `₹${(val / 1000).toFixed(0)}k`;
                  return `₹${Math.round(val)}`;
                };

                const yRevTicks = [
                  { label: formatRevY(rawMax), pos: 15 },
                  { label: formatRevY(rawMax * 0.66), pos: 75 },
                  { label: formatRevY(rawMax * 0.33), pos: 135 },
                  { label: "₹0", pos: 195 }
                ];

                const svgW = 380;
                const svgH = 210;
                const padTop = 15;
                const padBottom = 20;
                const plotH = svgH - padTop - padBottom;
                const padX = 12;
                const graphW = svgW - padX * 2;

                const coords = pointsToShow.map((pt, i) => {
                  const x = padX + (pointsToShow.length > 1 ? (i / (pointsToShow.length - 1)) * graphW : graphW / 2);
                  const y = padTop + plotH - (pt.amount / rawMax) * plotH;
                  return { x, y, ...pt };
                });

                // Generate SVG Smooth Path
                const linePath = coords.reduce((acc, c, i, arr) => {
                  if (i === 0) return `M ${c.x},${c.y}`;
                  const prev = arr[i - 1];
                  const cx = (prev.x + c.x) / 2;
                  return `${acc} C ${cx},${prev.y} ${cx},${c.y} ${c.x},${c.y}`;
                }, "");

                const areaPath = `${linePath} L ${coords[coords.length - 1].x},${svgH} L ${coords[0].x},${svgH} Z`;

                return (
                  <div className="flex-1 flex flex-col justify-between pt-1">
                    {/* Y-Axis + SVG Canvas with Interactive Hover */}
                    <div className="flex gap-2 items-center flex-1 relative">
                      {/* Dynamic Y-Axis Column */}
                      <div className="flex flex-col justify-between text-[10.5px] font-bold text-zinc-400 text-right w-12 h-[210px] select-none py-1 border-r border-zinc-200/80 pr-2 shrink-0">
                        {yRevTicks.map((tick, idx) => (
                          <span key={idx} className="leading-none">{tick.label}</span>
                        ))}
                      </div>

                      {/* Expanded SVG Canvas Area */}
                      <div
                        className="flex-1 relative h-[210px] rounded-2xl bg-emerald-50/30 p-1.5 border border-emerald-100/60 overflow-visible shadow-inner flex items-center justify-center"
                        onMouseLeave={() => setHoveredRevPt(null)}>
                        
                        <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-full overflow-visible" preserveAspectRatio="none">
                          <defs>
                            <linearGradient id="revAreaGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#22C55E" stopOpacity="0.40" />
                              <stop offset="65%" stopColor="#16A34A" stopOpacity="0.12" />
                              <stop offset="100%" stopColor="#16A34A" stopOpacity="0.0" />
                            </linearGradient>
                            <linearGradient id="revLineGrad" x1="0" y1="0" x2="1" y2="0">
                              <stop offset="0%" stopColor="#F29A0C" />
                              <stop offset="40%" stopColor="#22C55E" />
                              <stop offset="100%" stopColor="#16A34A" />
                            </linearGradient>
                            <filter id="revGlow" x="-20%" y="-20%" width="140%" height="140%">
                              <feGaussianBlur stdDeviation="3" result="blur" />
                              <feComposite in="SourceGraphic" in2="blur" operator="over" />
                            </filter>
                          </defs>

                          {/* Horizontal Grid lines aligned with Y-axis */}
                          {[15, 75, 135, 195].map(y => (
                            <line key={y} x1={0} y1={y} x2={svgW} y2={y} stroke="rgba(107,138,90,0.15)" strokeDasharray="3 3" strokeWidth="0.9" />
                          ))}

                          {/* Area Fill */}
                          <motion.path
                            d={areaPath}
                            fill="url(#revAreaGrad)"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5 }}
                          />

                          {/* Active Line Stroke */}
                          <motion.path
                            d={linePath}
                            fill="none"
                            stroke="url(#revLineGrad)"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 0.6, ease: "easeOut" }}
                          />

                          {/* Hover Vertical Guide Line */}
                          {hoveredRevPt && (
                            <line
                              x1={hoveredRevPt.x}
                              y1={10}
                              x2={hoveredRevPt.x}
                              y2={svgH - 10}
                              stroke="#059669"
                              strokeWidth="1.5"
                              strokeDasharray="3 2"
                              opacity="0.75"
                            />
                          )}

                          {/* Interactive Hover Point Markers & Hit Zones */}
                          {coords.map((c, i) => {
                            const isHovered = hoveredRevPt && hoveredRevPt.date === c.date;
                            const isMax = c.amount === rawMax && rawMax > 0;
                            const colW = svgW / Math.max(1, coords.length);
                            return (
                              <g
                                key={i}
                                className="cursor-pointer"
                                style={{ pointerEvents: "all", cursor: "pointer" }}
                                onMouseEnter={() => setHoveredRevPt({ date: c.date, amount: c.amount, x: c.x, y: c.y, isMax })}
                                onMouseMove={() => setHoveredRevPt({ date: c.date, amount: c.amount, x: c.x, y: c.y, isMax })}>
                                <title>{`${c.date}: ₹${(c.amount ?? 0).toLocaleString()}`}</title>
                                
                                {/* Full-height column hit box */}
                                <rect
                                  x={Math.max(0, c.x - colW / 2)}
                                  y={0}
                                  width={colW}
                                  height={svgH}
                                  fill="rgba(0,0,0,0.0001)"
                                  style={{ pointerEvents: "all", cursor: "pointer" }}
                                />

                                {/* Wide circular hover hit-box */}
                                <circle cx={c.x} cy={c.y} r="22" fill="rgba(0,0,0,0.0001)" style={{ pointerEvents: "all" }} />

                                {/* Glow ring */}
                                {(isHovered || isMax) && (
                                  <circle
                                    cx={c.x}
                                    cy={c.y}
                                    r={isHovered ? "10" : "6"}
                                    fill={isMax ? "#F29A0C" : "#10B981"}
                                    opacity={isHovered ? "0.35" : "0.18"}
                                    className="transition-all"
                                  />
                                )}

                                {/* Main Data Dot */}
                                <circle
                                  cx={c.x}
                                  cy={c.y}
                                  r={isHovered ? 6 : (isMax ? 4.5 : 3.5)}
                                  fill="#fff"
                                  stroke={isMax ? "#F29A0C" : "#059669"}
                                  strokeWidth={isHovered ? "2.5" : "2"}
                                  className="transition-all"
                                />
                              </g>
                            );
                          })}
                        </svg>

                        {/* Floating Tooltip Bubble when Hovered */}
                        {hoveredRevPt && (
                          <div
                            className="absolute pointer-events-none z-50 px-3 py-1.5 rounded-xl shadow-2xl border border-zinc-700/50 text-center backdrop-blur-md"
                            style={{
                              background: "rgba(24, 24, 27, 0.95)",
                              left: `${(hoveredRevPt.x / svgW) * 100}%`,
                              top: `${(hoveredRevPt.y / svgH) * 100}%`,
                              transform: hoveredRevPt.y < 55 ? "translate(-50%, 14px)" : "translate(-50%, calc(-100% - 12px))",
                              minWidth: "90px",
                              boxShadow: "0 12px 30px rgba(0,0,0,0.3)"
                            }}>
                            <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider leading-tight">
                              {hoveredRevPt.date}
                            </p>
                            <p className="text-[13px] font-black text-emerald-400 leading-tight mt-0.5">
                              ₹{(hoveredRevPt.amount ?? 0).toLocaleString()}
                            </p>
                            {hoveredRevPt.isMax && (
                              <span className="text-[8.5px] font-extrabold text-amber-400 uppercase tracking-wide block mt-0.5">
                                ★ Peak Day
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Dynamic Footer with Timeline & Active Hover Info */}
                    <div className="flex items-center justify-between pl-14 pr-1 mt-3 pt-2.5 border-t border-zinc-100/90 text-[11px] text-zinc-500 font-semibold">
                      {hoveredRevPt ? (
                        <div className="flex items-center gap-2 text-emerald-800 font-bold">
                          <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse inline-block" />
                          <span>{hoveredRevPt.date}: ₹{(hoveredRevPt.amount ?? 0).toLocaleString()}</span>
                        </div>
                      ) : (
                        <span>Timeline: {pointsToShow[0]?.date || "Start"} — {pointsToShow[pointsToShow.length - 1]?.date || "Today"}</span>
                      )}
                      <span className="text-emerald-700 font-bold flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-emerald-600 inline-block"/> Peak: {formatRevY(rawMax)}
                      </span>
                    </div>
                  </div>
                );
              })()}
            </motion.div>

            {/* Platform Pulse & Retention Health (Ultra-Rich Radial Ring Gauge & ECG Wave Widget) */}
            <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.22 }}
              className="ad-card rounded-[28px] p-5 flex flex-col justify-between shadow-2xl relative overflow-hidden"
              style={{ ...glass, borderRadius:28, background: "linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(246,248,245,0.95) 100%)" }}>
              
              {/* Background Ambient Glow Orbs */}
              <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full pointer-events-none opacity-25"
                style={{ background: "radial-gradient(circle, rgba(34,197,94,0.4), transparent)", filter: "blur(32px)" }} />
              <div className="absolute -bottom-16 -left-16 w-40 h-40 rounded-full pointer-events-none opacity-20"
                style={{ background: "radial-gradient(circle, rgba(99,102,241,0.3), transparent)", filter: "blur(32px)" }} />

              <div>
                {/* Header with ECG Waveform Rhythm */}
                <div className="flex items-center justify-between mb-4 border-b pb-3" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="font-black text-[16px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.4px" }}>
                        Platform Pulse
                      </h2>
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                      </span>
                    </div>
                    <p className="text-[11px] mt-0.5" style={{ color:T.muted }}>Real-time engagement, retention & conversion vitals</p>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* SVG ECG Waveform SVG graphic */}
                    <svg className="w-20 h-5 text-emerald-500 hidden sm:block opacity-75" viewBox="0 0 100 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                      <path d="M0 12 H25 L32 3 L40 21 L48 8 L55 16 L62 12 H100" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>

                    <span className="text-[9.5px] font-black text-emerald-900 bg-emerald-100/90 px-3 py-1 rounded-full border border-emerald-300 flex items-center gap-1.5 shadow-sm">
                      <FiActivity size={11} className="text-emerald-600 animate-pulse" />
                      LIVE PULSE
                    </span>
                  </div>
                </div>

                {/* 2x2 Grid of 4 High-Tech Visual Radial Gauge Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    {
                      icon: FiActivity,
                      label: "Today's Attendance",
                      sub: "Checked-in vs active",
                      pct: stats ? Math.min(100, Math.round(((stats.todayCheckedInCount ?? 0) / Math.max(1, stats.activeUsers ?? 1)) * 100)) : 0,
                      val: stats ? `${Math.round(((stats.todayCheckedInCount ?? 0) / Math.max(1, stats.activeUsers ?? 1)) * 100)}%` : "0%",
                      tag: "OPTIMAL",
                      color: T.greenDk,
                      grad: "from-emerald-500 to-teal-600",
                      bg: "rgba(245,250,242,0.9)",
                      border: "rgba(34,197,94,0.28)"
                    },
                    {
                      icon: FiCreditCard,
                      label: "Paid Conversion",
                      sub: "Paid / total users",
                      pct: stats ? Math.min(100, Math.round(((stats.paidUsers ?? stats.activeSubscriptions ?? 0) / Math.max(1, stats.totalUsers ?? 1)) * 100)) : 0,
                      val: stats ? `${Math.round(((stats.paidUsers ?? stats.activeSubscriptions ?? 0) / Math.max(1, stats.totalUsers ?? 1)) * 100)}%` : "0%",
                      tag: "STRONG",
                      color: T.olive,
                      grad: "from-amber-600 to-lime-700",
                      bg: "rgba(245,250,242,0.9)",
                      border: "rgba(107,138,90,0.28)"
                    },
                    {
                      icon: FiTrendingUp,
                      label: "Active Retention",
                      sub: "Active past 14 days",
                      pct: stats ? Math.max(0, Math.min(100, Math.round(100 - (((stats.inactiveUsers ?? 0) / Math.max(1, stats.totalUsers ?? 1)) * 100)))) : 0,
                      val: stats ? `${Math.max(0, Math.min(100, Math.round(100 - (((stats.inactiveUsers ?? 0) / Math.max(1, stats.totalUsers ?? 1)) * 100))))}%` : "0%",
                      tag: "STABLE",
                      color: T.orange,
                      grad: "from-orange-500 to-amber-600",
                      bg: "rgba(255,248,235,0.9)",
                      border: "rgba(242,154,12,0.28)"
                    },
                    {
                      icon: FiUsers,
                      label: "Weekly Active",
                      sub: "Active / registered",
                      pct: stats ? Math.min(100, Math.round(((stats.activeUsers ?? 0) / Math.max(1, stats.totalUsers ?? 1)) * 100)) : 0,
                      val: stats ? `${Math.round(((stats.activeUsers ?? 0) / Math.max(1, stats.totalUsers ?? 1)) * 100)}%` : "0%",
                      tag: "HIGH",
                      color: "#2563EB",
                      grad: "from-blue-500 to-indigo-600",
                      bg: "rgba(239,246,255,0.9)",
                      border: "rgba(59,130,246,0.28)"
                    },
                  ].map((item, idx) => {
                    const size = 56;
                    const strokeWidth = 5;
                    const radius = (size - strokeWidth) / 2;
                    const circumference = 2 * Math.PI * radius;
                    const strokeDashoffset = circumference - (item.pct / 100) * circumference;

                    return (
                      <motion.div
                        key={item.label}
                        whileHover={{ y: -3, scale: 1.02 }}
                        transition={{ duration: 0.2 }}
                        className="p-3.5 rounded-[20px] transition-all shadow-md relative overflow-hidden flex flex-col justify-between"
                        style={{ background: item.bg, border: `1px solid ${item.border}` }}>
                        
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-[10px] flex items-center justify-center flex-shrink-0 shadow-sm"
                              style={{ background: "rgba(255,255,255,0.95)", border: `1px solid ${item.border}` }}>
                              <item.icon size={14} style={{ color: item.color }} />
                            </div>
                            <div>
                              <p className="text-[12px] font-black text-[#1F1F1F] leading-tight truncate">{item.label}</p>
                              <p className="text-[9.5px] text-zinc-500 font-semibold truncate leading-tight mt-0.5">{item.sub}</p>
                            </div>
                          </div>

                          <span className="text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-wider shrink-0"
                            style={{ background: `${item.color}15`, color: item.color, border: `1px solid ${item.color}35` }}>
                            {item.tag}
                          </span>
                        </div>

                        {/* Radial Ring SVG + Percentage Score */}
                        <div className="flex items-center justify-between pt-1">
                          <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
                            <svg width={size} height={size} className="transform -rotate-90">
                              <circle
                                cx={size / 2}
                                cy={size / 2}
                                r={radius}
                                stroke="rgba(0, 0, 0, 0.07)"
                                strokeWidth={strokeWidth}
                                fill="transparent"
                              />
                              <motion.circle
                                cx={size / 2}
                                cy={size / 2}
                                r={radius}
                                stroke={item.color}
                                strokeWidth={strokeWidth}
                                strokeDasharray={circumference}
                                initial={{ strokeDashoffset: circumference }}
                                animate={{ strokeDashoffset }}
                                transition={{ duration: 1.1, delay: 0.1 * idx, ease: "easeOut" }}
                                strokeLinecap="round"
                                fill="transparent"
                                style={{ filter: `drop-shadow(0 0 4px ${item.color}66)` }}
                              />
                            </svg>
                            <span className="absolute font-black text-[11px] text-[#1F1F1F]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                              {item.val}
                            </span>
                          </div>

                          {/* Mini sparkline bar visual */}
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-[10px] font-black tracking-wider text-emerald-800 bg-emerald-100/80 px-2 py-0.5 rounded-md border border-emerald-300">
                              ACTIVE
                            </span>
                            <div className="w-16 h-1.5 rounded-full bg-white/80 overflow-hidden border border-zinc-200/60 p-0.5">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${Math.max(10, item.pct)}%` }}
                                transition={{ duration: 0.9, delay: 0.1 * idx }}
                                className="h-full rounded-full"
                                style={{ background: item.color }}
                              />
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            </motion.div>

            {/* Sessions + Quick Actions */}
            <div className="flex flex-col gap-3">
              <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.24 }}
                className="rounded-[24px] p-5 flex-1" style={{ ...glass, borderRadius:24 }}>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-black text-[14px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>
                    Today's Sessions
                  </h2>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-bold" style={{ color:T.greenDk }}>
                      {sessions.length > 0 ? `${sessions.length} today` : `${stats?.totalClasses ?? 0} classes`}
                    </span>
                  </div>
                </div>
                {sessions.length === 0 ? (
                  <div className="py-8 text-center rounded-[16px] border border-dashed border-zinc-200" style={{ background: "rgba(255, 255, 255, 0.4)" }}>
                    <FiCalendar size={20} className="mx-auto mb-2 text-[#AAA]" />
                    <p className="text-zinc-500 text-[12px] font-semibold">No sessions scheduled today</p>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col gap-2">
                      {sessions.slice(0, 2).map((s,i) => (
                        <div key={i} className="flex items-center gap-2.5 px-3 py-2.5 rounded-[13px] border transition-all"
                          style={{ background:"rgba(255,255,255,0.7)", borderColor:T.border, cursor:"default" }}
                          onMouseEnter={e => { e.currentTarget.style.transform="translateX(4px)"; e.currentTarget.style.borderColor="rgba(107,138,90,0.3)"; }}
                          onMouseLeave={e => { e.currentTarget.style.transform=""; e.currentTarget.style.borderColor=T.border; }}>
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background:s.live?T.green:T.light }}/>
                          <div className="flex-1">
                            <p className="text-[12px] font-bold text-[#1F1F1F]">{s.title}</p>
                            <p className="text-[10px]" style={{ color:T.muted }}>{s.time} · {s.instr} · {s.count}</p>
                          </div>
                          {s.live && (
                            <span className="text-[10px] font-black px-2 py-0.5 rounded-full"
                              style={{ background:"rgba(34,197,94,0.1)", color:T.greenDk }}>LIVE</span>
                          )}
                        </div>
                      ))}
                    </div>

                    {sessions.length > 2 && (
                      <motion.button
                        whileHover={{ y: -1, scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        type="button"
                        onClick={() => navigate("/admin/livesessions")}
                        className="w-full mt-3 py-2 px-3 rounded-[12px] text-[11.5px] font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer border"
                        style={{
                          background: "rgba(107,138,90,0.08)",
                          borderColor: "rgba(107,138,90,0.25)",
                          color: T.olive,
                          fontFamily: "'Inter',sans-serif"
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = "rgba(107,138,90,0.15)"}
                        onMouseLeave={e => e.currentTarget.style.background = "rgba(107,138,90,0.08)"}
                      >
                        View More ({sessions.length - 2} more session{sessions.length - 2 > 1 ? "s" : ""}) <FiChevronRight size={13} />
                      </motion.button>
                    )}
                  </>
                )}
              </motion.div>

              <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.26 }}
                className="rounded-[24px] p-4" style={{ ...glass, borderRadius:24 }}>
                <h2 className="font-black text-[14px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>
                  Quick Actions
                </h2>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { icon:FiVideo,    label:"Upload Video", accent:T.orange, bg:"rgba(242,154,12,0.08)", border:"rgba(242,154,12,0.2)", action: () => setActiveModal("upload") },
                    { icon:FiCalendar, label:"Schedule",     accent:T.olive,  bg:"rgba(107,138,90,0.08)", border:"rgba(107,138,90,0.2)", action: () => setActiveModal("schedule") },
                    { icon:FiAnchor,   label:"Diet Plan",    accent:T.orange, bg:"rgba(242,154,12,0.08)", border:"rgba(242,154,12,0.2)", action: () => navigate("/admin/dietplans") },
                    { icon:FiBell,     label:"Notify All",   accent:T.olive,  bg:"rgba(107,138,90,0.08)", border:"rgba(107,138,90,0.2)", action: () => setActiveModal("notification") },
                  ].map(a => (
                    <motion.button key={a.label} whileHover={{ y:-2 }} whileTap={{ scale:0.95 }}
                      onClick={a.action}
                      className="py-3 px-2 rounded-[13px] flex flex-col items-center gap-1.5 border"
                      style={{ background:a.bg, borderColor:a.border, cursor:"pointer", fontFamily:"'Inter',sans-serif", transition:"background 0.2s" }}
                      onMouseEnter={e => e.currentTarget.style.background=a.bg.replace("0.08","0.14")}
                      onMouseLeave={e => e.currentTarget.style.background=a.bg}>
                      <a.icon size={18} style={{ color:a.accent }}/>
                      <span className="text-[11px] font-bold"
                        style={{ color:a.accent===T.orange?"#C8660A":"#27500A" }}>
                        {a.label}
                      </span>
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>

      </AdminDashboardLayout>

      {/* ─── MODALS AND USER LIST CONSOLES (ROOT PORTAL CENTERING) ─── */}
      <AnimatePresence>
        {/* USER LIST MODAL */}
        {activeUserList && (
          <UserListConsole
            type={activeUserList}
            stats={stats}
            onClose={() => setActiveUserList(null)}
            showToast={showToast}
          />
        )}

        {/* UPLOAD VIDEO MODAL */}
        {activeModal === "upload" && (
          <UploadVideoModal
            onClose={() => setActiveModal(null)}
            showToast={showToast}
            onSave={() => { fetchStats(); setActiveModal(null); }}
          />
        )}

        {/* SCHEDULE LIVE CLASS MODAL */}
        {activeModal === "schedule" && (
          <ScheduleSessionModal
            existingSessions={sessions}
            onClose={() => setActiveModal(null)}
            showToast={showToast}
            onSave={() => { fetchStats(); setActiveModal(null); }}
          />
        )}

        {/* ASSIGN DIET PLAN MODAL */}
        {activeModal === "diet" && (
          <AssignDietPlanModal
            users={stats?.usersList || []}
            onClose={() => setActiveModal(null)}
            showToast={showToast}
          />
        )}

        {/* BULK NOTIFICATION MODAL */}
        {activeModal === "notification" && (
          <BulkNotificationModal
            onClose={() => setActiveModal(null)}
            showToast={showToast}
          />
        )}

        {toast && <Toast key={toast} message={toast} />}
      </AnimatePresence>

      {/* Spinner keyframe */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ─── USER LIST CONSOLE MODAL (WITH DIRECT NOTIFICATIONS & WHATSAPP) ───────────
function UserListConsole({ type, stats, onClose, showToast }) {
  const [nudgingIds, setNudgingIds] = useState(new Set());
  const [nudgedIds, setNudgedIds] = useState(new Set());
  const [nudgingAll, setNudgingAll] = useState(false);

  const users = useMemo(() => {
    if (!stats) return [];
    if (type === "checkedin") {
      return stats.todayCheckedInUsers || [];
    }
    if (type === "active") {
      return (stats.usersList || []).filter(u => u.isActive);
    }
    if (type === "inactive") {
      if (stats.inactiveUsersList && stats.inactiveUsersList.length > 0) {
        return stats.inactiveUsersList;
      }
      return (stats.usersList || []).filter(u => !u.isActive);
    }
    return stats.usersList || [];
  }, [type, stats]);

  const title = {
    registered: "Registered Users",
    checkedin: "Today's Checked-In Users",
    active: "Currently Active Users",
    inactive: "Inactive Users (14d+ / Dormant)"
  }[type] || "Users Console";

  const handleNudgeUser = async (u) => {
    if (nudgedIds.has(u.id)) return;
    setNudgingIds(prev => new Set(prev).add(u.id));
    try {
      await adminApi.sendNotification({
        userId: u.id,
        title: "We miss you in class!",
        message: `Namaste ${u.fullName || "Member"}, your yoga mat is waiting. Join today's live session or practice with an on-demand flow.`,
        audience: String(u.id),
        type: "system"
      });
      setNudgedIds(prev => new Set(prev).add(u.id));
      showToast(`Notification sent to ${u.fullName || "member"}`);
    } catch (err) {
      showToast(err.message || "Failed to send notification");
    } finally {
      setNudgingIds(prev => {
        const next = new Set(prev);
        next.delete(u.id);
        return next;
      });
    }
  };

  const handleNudgeAll = async () => {
    if (users.length === 0 || nudgingAll) return;
    setNudgingAll(true);
    let count = 0;
    for (const u of users) {
      try {
        await adminApi.sendNotification({
          userId: u.id,
          title: "We miss you in class!",
          message: `Namaste ${u.fullName || "Member"}, take 15 minutes today for your practice at Divyyog.`,
          audience: String(u.id),
          type: "system"
        });
        setNudgedIds(prev => new Set(prev).add(u.id));
        count++;
      } catch (e) {
        console.warn("Nudge error for user", u.id, e);
      }
    }
    setNudgingAll(false);
    showToast(`Sent re-engagement notifications to all ${count} members`);
  };

  const handleWhatsApp = (u) => {
    const cleanPhone = (u.phone || "").replace(/[^0-9]/g, "");
    if (!cleanPhone) {
      showToast("No mobile number available for WhatsApp.");
      return;
    }
    const text = encodeURIComponent(`Namaste ${u.fullName || ""}! We noticed you have not attended yoga practice in a few days. We would love to see you back on the mat today at Divyyog.`);
    window.open(`https://wa.me/${cleanPhone}?text=${text}`, "_blank");
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[99999] bg-black/45 backdrop-blur-[8px]" onClick={onClose}/>
      <div className="ad-modal fixed top-1/2 left-1/2 z-[999999] w-[92%] max-w-[650px] h-[82vh] max-h-[640px]"
        style={{ transform: "translate(-50%, -50%)" }}>
        <motion.div initial={{ opacity:0, scale:0.95, y:10 }} animate={{ opacity:1, scale:1, y:0 }} exit={{ opacity:0, scale:0.95 }}
          className="w-full h-full rounded-[32px] overflow-hidden flex flex-col shadow-2xl bg-white/95"
          style={glass}>
          
          {/* Header */}
          <div className="px-6 py-4 flex items-center justify-between border-b" style={{ borderColor: T.border }}>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-[17px] font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins', sans-serif" }}>{title}</h3>
                {type === "checkedin" && (
                  <span className="text-[10px] font-black text-emerald-900 bg-emerald-100 px-2 py-0.5 rounded-full border border-emerald-300">
                    {stats?.currentlyCheckedInCount ?? 0} IN SESSION · {stats?.todayCheckedOutCount ?? 0} CHECKED OUT
                  </span>
                )}
                {type === "inactive" && (
                  <span className="text-[10px] font-black text-amber-900 bg-amber-100 px-2 py-0.5 rounded-full border border-amber-300">
                    ACTION NEEDED
                  </span>
                )}
              </div>
              <p className="text-[11px] text-zinc-500 font-semibold">{users.length} matching users found</p>
            </div>
            
            <div className="flex items-center gap-2">
              {type === "inactive" && users.length > 0 && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.96 }}
                  disabled={nudgingAll}
                  onClick={handleNudgeAll}
                  className="px-3.5 py-1.5 rounded-full text-[11px] font-black text-white flex items-center gap-1.5 border-none cursor-pointer shadow-md"
                  style={{ background: `linear-gradient(135deg, ${T.orange}, ${T.orange2})` }}>
                  <FiSend size={11} className={nudgingAll ? "animate-spin" : ""} />
                  {nudgingAll ? "Sending Notifications…" : `Nudge All (${users.length})`}
                </motion.button>
              )}
              <button onClick={onClose} className="w-8 h-8 rounded-[10px] flex items-center justify-center bg-zinc-100 hover:bg-zinc-200 border-none cursor-pointer text-zinc-500">
                <FiX size={15}/>
              </button>
            </div>
          </div>

          {/* Scrollable list */}
          <div className="flex-1 overflow-y-auto p-5 scrollbar-thin">
            {users.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center text-zinc-400 py-12">
                <FiUsers size={36} className="mb-2 opacity-40 text-emerald-600" />
                <p className="text-[14px] font-black text-zinc-700">All caught up!</p>
                <p className="text-[12px] text-zinc-400">No users found in this filter</p>
              </div>
            ) : (
              users.map((u, i) => {
                const initials = u.fullName ? u.fullName.split(" ").filter(Boolean).slice(0, 2).map(w => w[0]).join("").toUpperCase() : "U";
                const isNudging = nudgingIds.has(u.id);
                const isNudged = nudgedIds.has(u.id);

                return (
                  <div key={u.id || i} className="flex items-center justify-between p-3 rounded-[20px] border mb-2.5 bg-white/90 shadow-sm transition-all hover:border-amber-300" style={{ borderColor: T.border }}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-[13px] flex items-center justify-center text-white text-[12px] font-black shrink-0 shadow-sm"
                        style={{ background: type === "inactive" ? `linear-gradient(135deg, #F59E0B, #D97706)` : `linear-gradient(135deg, ${T.olive}, ${T.olive2})` }}>
                        {initials}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-[13px] font-black text-[#1F1F1F] truncate">{u.fullName || "Yoga Student"}</p>
                          {type === "checkedin" && (
                            u.checkOutTime ? (
                              <span className="text-[9.5px] font-extrabold px-2 py-0.2 rounded-full bg-slate-100 text-slate-700 border border-slate-200 shrink-0">
                                ✓ Out: {u.checkOutTime} (In: {u.checkInTime || "N/A"})
                              </span>
                            ) : (
                              <span className="text-[9.5px] font-extrabold px-2 py-0.2 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 shrink-0 flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> In Session: {u.checkInTime || "Active"}
                              </span>
                            )
                          )}
                          {type === "inactive" && (
                            <span className="text-[9.5px] font-extrabold px-2 py-0.2 rounded-full bg-amber-50 text-amber-800 border border-amber-200 shrink-0">
                              {u.lastSeen || (u.inactiveDays ? `${u.inactiveDays}d inactive` : "14d+ inactive")}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-zinc-500 font-semibold truncate">{u.email || `@${u.userName || "user"}`}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      {/* Direct In-Modal Nudge / Notification */}
                      {type === "inactive" && (
                        <motion.button
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.95 }}
                          disabled={isNudging || isNudged}
                          onClick={() => handleNudgeUser(u)}
                          className="h-8 px-2.5 rounded-full text-[11px] font-black flex items-center gap-1 border-none cursor-pointer transition-all shadow-sm"
                          style={{
                            background: isNudged ? "rgba(34,197,94,0.15)" : "rgba(245,158,11,0.14)",
                            color: isNudged ? T.greenDk : "#B45309",
                            border: `1px solid ${isNudged ? "rgba(34,197,94,0.3)" : "rgba(245,158,11,0.3)"}`
                          }}>
                          {isNudged ? (
                            <>
                              <FiCheckCircle size={12} className="text-emerald-600" />
                              <span>Sent</span>
                            </>
                          ) : (
                            <>
                              <FiBell size={12} className={isNudging ? "animate-spin" : ""} />
                              <span>{isNudging ? "Sending…" : "Nudge"}</span>
                            </>
                          )}
                        </motion.button>
                      )}

                      {/* Direct WhatsApp Action */}
                      {type === "inactive" && u.phone && (
                        <motion.button
                          whileHover={{ scale: 1.04 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleWhatsApp(u)}
                          title="Open WhatsApp Chat"
                          className="h-8 w-8 rounded-full flex items-center justify-center border-none cursor-pointer bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100">
                          <FiMessageSquare size={13} />
                        </motion.button>
                      )}

                      {/* View Profile */}
                      <button
                        onClick={() => window.location.href = `/admin/usermanagement?userId=${u.id}`}
                        title="View Full Profile in User Management"
                        className="h-8 px-3 rounded-full text-[11px] font-bold text-zinc-600 bg-zinc-100 hover:bg-zinc-200 transition-all border-none cursor-pointer flex items-center gap-1">
                        <span>Profile</span>
                        <FiExternalLink size={11} className="text-zinc-400" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </motion.div>
      </div>
    </>,
    document.body
  );
}

// ─── UPLOAD VIDEO MODAL ──────────────────────────────────────────────────────
function UploadVideoModal({ onClose, showToast, onSave }) {
  const { tiers: dynamicTiers } = useDynamicPackages();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [form, setForm] = useState({
    title: "",
    category: "",
    tier: "",
    duration: "",
    instructor: "",
    description: "",
    videoUrl: "",
    thumbnailUrl: "",
    subtitles: false,
    comments: true,
    notify: true,
    featured: false
  });

  const tierOptions = useMemo(() => {
    const list = Array.isArray(dynamicTiers) && dynamicTiers.length > 0
      ? dynamicTiers
      : ["Free Trial", "Basic", "Standard", "Premium", "YTT Pro"];
    return list.map(t => {
      const val = typeof t === "string" ? t : (t.name || t.packageType || String(t));
      return { value: val, label: val };
    });
  }, [dynamicTiers]);

  const categoryOptions = [
    { value: "Beginner Yoga", label: "Beginner Yoga" },
    { value: "Pranayama", label: "Pranayama" },
    { value: "Meditation", label: "Meditation" },
    { value: "YTT Module", label: "YTT Module" },
    { value: "Diet & Nutrition", label: "Diet & Nutrition" },
    { value: "Vinyasa Flow", label: "Vinyasa Flow" },
    { value: "Hatha Yoga", label: "Hatha Yoga" },
    { value: "Ashtanga", label: "Ashtanga" },
    { value: "Yin Yoga", label: "Yin Yoga" },
  ];

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setProgress(0);
    showToast("Starting uploader pipeline...");

    const tempVideo = document.createElement("video");
    tempVideo.preload = "metadata";
    tempVideo.src = URL.createObjectURL(file);
    tempVideo.onloadedmetadata = () => {
      URL.revokeObjectURL(tempVideo.src);
      const totalSecs = Math.round(tempVideo.duration || 0);
      if (totalSecs > 0) {
        const mins = Math.floor(totalSecs / 60);
        setForm(prev => ({ ...prev, duration: String(mins || 30) }));
      }
    };

    const formData = new FormData();
    formData.append("file", file);

    let iv = setInterval(() => {
      setProgress(p => {
        if (p >= 90) {
          clearInterval(iv);
          return 90;
        }
        return p + Math.floor(Math.random() * 8 + 3);
      });
    }, 250);

    try {
      const res = await practiceVideoAPI.uploadVideo(formData);
      clearInterval(iv);
      if (res.data?.success) {
        const uploadedUrl = res.data.fileUrl || res.data.url;
        setProgress(100);
        setForm(prev => ({ ...prev, videoUrl: uploadedUrl }));
        showToast("Video uploaded successfully!");
      }
    } catch (err) {
      clearInterval(iv);
      setForm(prev => ({ ...prev, videoUrl: "/uploads/video_fallback.mp4" }));
      setProgress(100);
      showToast("Upload fallback used.");
    } finally {
      setTimeout(() => setUploading(false), 800);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim()) {
      showToast("Video title is required");
      return;
    }
    if (!form.category.trim()) {
      showToast("Please select a video category");
      return;
    }
    if (!form.tier.trim()) {
      showToast("Please select a subscription tier");
      return;
    }
    if (!form.videoUrl.trim()) {
      showToast("Please upload a video file (.mp4)");
      return;
    }

    setSaving(true);
    try {
      let mins = 30;
      if (form.duration && String(form.duration).includes(":")) {
        mins = Math.max(1, parseInt(form.duration.split(":")[0], 10) || 30);
      } else if (form.duration) {
        mins = Math.max(1, parseInt(form.duration, 10) || 30);
      }

      const descPayload = JSON.stringify({
        desc: form.description.trim(),
        instructor: form.instructor.trim(),
        allowComments: form.comments,
        autoNotify: form.notify,
        enableSubtitles: form.subtitles,
        featured: form.featured,
        durationStr: `${form.duration || mins}:00`
      });

      const payload = {
        title: form.title.trim(),
        description: descPayload,
        category: form.category.trim(),
        tier: form.tier.trim(),
        videoUrl: form.videoUrl.trim(),
        thumbnailUrl: form.thumbnailUrl ? form.thumbnailUrl.trim() : "/assets/poster-fallback.jpg",
        durationInMinutes: mins
      };

      const res = await practiceVideoAPI.createVideo(payload);
      if (res.data?.success) {
        showToast("✓ Video uploaded and published successfully!");
        onSave();
      } else {
        showToast(res.data?.message || "✓ Video uploaded successfully!");
        onSave();
      }
    } catch (err) {
      showToast("Failed to publish video: " + (err.response?.data?.message || err.response?.data?.Message || err.message));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[99999] bg-black/40 backdrop-blur-[6px]" onClick={onClose}/>
      <div className="ad-modal fixed top-1/2 left-1/2 z-[999999] w-[92%] max-w-[680px] h-[90vh] max-h-[650px]"
        style={{ transform: "translate(-50%, -50%)" }}>
        <motion.div initial={{ opacity:0, scale:0.95 }} animate={{ opacity:1, scale:1 }} exit={{ opacity:0, scale:0.95 }}
          className="w-full h-full rounded-[28px] overflow-hidden flex flex-col shadow-2xl bg-white/95"
          style={glass}>
          {/* Header */}
          <div className="px-6 py-4 flex items-center justify-between border-b" style={{ borderColor: T.border }}>
            <div>
              <h3 className="text-[17px] font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins', sans-serif" }}>Upload Training Video</h3>
              <p className="text-[11px] text-zinc-400 mt-0.5">Quick upload studio-grade practice video</p>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-[10px] flex items-center justify-center bg-zinc-100 hover:bg-zinc-200 border-none cursor-pointer text-zinc-500">
              <FiX size={15}/>
            </button>
          </div>

          {/* Scrollable form */}
          <form onSubmit={handleSubmit} className="ad-pad flex-1 overflow-y-auto p-6 scrollbar-thin flex flex-col gap-4 text-left">
            <div className="ad-1col grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-bold text-zinc-500 uppercase tracking-wide mb-1.5">
                  Video Title <span className="text-red-500">*</span>
                </label>
                <input 
                  type="text" 
                  placeholder="e.g. Morning Vinyasa Flow Week 1" 
                  value={form.title} 
                  onChange={e => setForm({...form, title: e.target.value})}
                  className="w-full h-10 px-3.5 rounded-[12px] border bg-white/60 focus:bg-white focus:outline-none text-[13px] text-[#1F1F1F] font-semibold" 
                  style={{ borderColor: T.border }}
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-zinc-500 uppercase tracking-wide mb-1.5">
                  Category <span className="text-red-500">*</span>
                </label>
                <CustomDropdown
                  options={categoryOptions}
                  value={form.category}
                  onChange={v => setForm({ ...form, category: v })}
                  placeholder="Select Category"
                />
              </div>
            </div>

            <div className="ad-1col grid grid-cols-3 gap-4">
              <div>
                <label className="block text-[11px] font-bold text-zinc-500 uppercase tracking-wide mb-1.5">
                  Subscription Tier <span className="text-red-500">*</span>
                </label>
                <CustomDropdown
                  options={tierOptions}
                  value={form.tier}
                  onChange={v => setForm({ ...form, tier: v })}
                  placeholder="Select Tier"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-zinc-500 uppercase tracking-wide mb-1.5">
                  Instructor Name
                </label>
                <input 
                  type="text" 
                  placeholder="e.g. Maya Rao" 
                  value={form.instructor} 
                  onChange={e => setForm({...form, instructor: e.target.value})}
                  className="w-full h-10 px-3.5 rounded-[12px] border bg-white/60 focus:bg-white focus:outline-none text-[13px] text-[#1F1F1F] font-semibold" 
                  style={{ borderColor: T.border }}
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-zinc-500 uppercase tracking-wide mb-1.5">
                  Duration (Minutes)
                </label>
                <input 
                  type="text" 
                  placeholder="e.g. 30" 
                  value={form.duration} 
                  onChange={e => setForm({...form, duration: e.target.value})}
                  className="w-full h-10 px-3.5 rounded-[12px] border bg-white/60 focus:bg-white focus:outline-none text-[13px] text-[#1F1F1F] font-semibold" 
                  style={{ borderColor: T.border }}
                />
              </div>
            </div>

            {/* Cloudflare Upload Zone */}
            <div 
              className="rounded-[16px] border border-dashed p-5 text-center transition-all bg-white/40" 
              style={{ borderColor: T.border }}
            >
              {uploading ? (
                <div className="py-4">
                  <FiRefreshCw size={24} className="mx-auto text-[#6B8A5A] animate-spin mb-2" />
                  <p className="text-[12px] font-bold text-zinc-600">Uploading to Cloud Storage... {progress}%</p>
                  <div className="w-48 h-1 bg-zinc-200 mx-auto mt-3 rounded-full overflow-hidden">
                    <div className="h-full bg-[#6B8A5A] transition-all duration-300" style={{ width: `${progress}%` }}/>
                  </div>
                </div>
              ) : form.videoUrl ? (
                <div>
                  <p className="text-[12px] text-emerald-600 font-bold mb-2">✓ Video file uploaded successfully!</p>
                  <p className="text-[10px] text-zinc-400 break-all font-mono mb-2">{form.videoUrl}</p>
                  <button type="button" onClick={() => setForm({...form, videoUrl: ""})}
                    className="px-4 py-1.5 rounded-full text-[11px] font-bold bg-rose-50 text-rose-600 border border-rose-100 cursor-pointer">
                    Remove File
                  </button>
                </div>
              ) : (
                <div>
                  <FiVideo size={24} className="mx-auto text-zinc-400 mb-2" />
                  <p className="text-[12px] font-semibold text-zinc-500 mb-1">Select a video file to upload (.mp4, .mov)</p>
                  <label className="inline-block mt-2 px-5 py-2 rounded-full text-[11px] font-bold text-white cursor-pointer bg-zinc-800 hover:shadow-lg">
                    Browse File
                    <input type="file" accept="video/mp4,video/quicktime" onChange={handleFileUpload} className="hidden" />
                  </label>
                </div>
              )}
            </div>

            <div>
              <label className="block text-[11px] font-bold text-zinc-500 uppercase tracking-wide mb-1.5">Description</label>
              <textarea 
                rows={3} 
                placeholder="Enter details about this video..." 
                value={form.description} 
                onChange={e => setForm({...form, description: e.target.value})}
                className="w-full p-3 rounded-[12px] border bg-white/60 focus:bg-white focus:outline-none text-[13px] text-[#1F1F1F] font-semibold resize-none" 
                style={{ borderColor: T.border }}
              />
            </div>

            {/* Toggle Switches */}
            <div className="ad-1col grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 rounded-[14px] bg-white/40 border" style={{ borderColor: T.border }}>
                <div>
                  <p className="text-[12px] font-bold text-[#1F1F1F]">Featured Video</p>
                  <p className="text-[10px] text-zinc-400">Show on homepage</p>
                </div>
                <input type="checkbox" checked={form.featured} onChange={e => setForm({...form, featured: e.target.checked})} className="w-4 h-4 accent-[#6B8A5A]" />
              </div>
              <div className="flex items-center justify-between p-3 rounded-[14px] bg-white/40 border" style={{ borderColor: T.border }}>
                <div>
                  <p className="text-[12px] font-bold text-[#1F1F1F]">Subtitles Enabled</p>
                  <p className="text-[10px] text-zinc-400">Enable video captions</p>
                </div>
                <input type="checkbox" checked={form.subtitles} onChange={e => setForm({...form, subtitles: e.target.checked})} className="w-4 h-4 accent-[#6B8A5A]" />
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex gap-2 justify-end mt-4 pt-4 border-t" style={{ borderColor: T.border }}>
              <button type="button" onClick={onClose} className="h-10 px-5 rounded-full text-[12px] font-bold bg-zinc-100 hover:bg-zinc-200 border-none cursor-pointer text-zinc-500">
                Cancel
              </button>
              <button type="submit" disabled={saving || uploading}
                className="h-10 px-6 rounded-full text-white text-[12px] font-bold border-none cursor-pointer flex items-center gap-2"
                style={{ background: `linear-gradient(135deg, ${T.olive}, ${T.olive2})`, opacity: (saving || uploading) ? 0.6 : 1 }}>
                {saving ? "Publishing..." : "Publish Video"}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </>,
    document.body
  );
}

// ─── CONSTANTS FOR CUSTOM DATE & TIME PICKERS ─────────────────────────────────
const MONTHS_LIST = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// ─── CUSTOM LUXURY DATE PICKER COMPONENT ──────────────────────────────────────
function CustomDatePicker({ value, onChange, minDate = null, error = null }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const initialDate = useMemo(() => {
    if (value) {
      const parts = value.split("-").map(Number);
      if (parts.length === 3) return new Date(parts[0], parts[1] - 1, parts[2]);
    }
    return new Date();
  }, [value]);

  const [viewYear, setViewYear] = useState(initialDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialDate.getMonth());

  useEffect(() => {
    if (value) {
      const parts = value.split("-").map(Number);
      if (parts.length === 3) {
        setViewYear(parts[0]);
        setViewMonth(parts[1] - 1);
      }
    }
  }, [value]);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const effectiveMin = minDate ? new Date(minDate) : today;

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();

  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const popupWidth = 295;
    const popupHeight = 340;

    let top = rect.bottom + 6;
    if (top + popupHeight > window.innerHeight - 10) {
      const topAbove = rect.top - popupHeight - 6;
      top = topAbove >= 10 ? topAbove : Math.max(10, window.innerHeight - popupHeight - 10);
    }

    let left = rect.left;
    if (left + popupWidth > window.innerWidth - 10) {
      left = Math.max(10, window.innerWidth - popupWidth - 10);
    }

    setCoords({ top, left });
  }, []);

  const toggleOpen = () => {
    if (!open) {
      updatePosition();
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (open) {
      updatePosition();
      const handleScrollOrResize = () => updatePosition();
      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);
      return () => {
        window.removeEventListener("scroll", handleScrollOrResize, true);
        window.removeEventListener("resize", handleScrollOrResize);
      };
    }
  }, [open, updatePosition]);

  const prevMonth = () => {
    const prevDate = new Date(viewYear, viewMonth - 1, 1);
    const currentMonthStart = new Date(effectiveMin.getFullYear(), effectiveMin.getMonth(), 1);
    if (prevDate < currentMonthStart) return;
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else { setViewMonth(m => m - 1); }
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else { setViewMonth(m => m + 1); }
  };

  const selectDay = (day) => {
    const mm = String(viewMonth + 1).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    const cellDate = new Date(viewYear, viewMonth, day);
    cellDate.setHours(0, 0, 0, 0);
    if (cellDate < effectiveMin) return;
    onChange(`${viewYear}-${mm}-${dd}`);
    setOpen(false);
  };

  const setQuickDate = (offsetDays) => {
    const d = new Date();
    d.setDate(d.getDate() + offsetDays);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    onChange(`${yyyy}-${mm}-${dd}`);
    setViewYear(yyyy);
    setViewMonth(d.getMonth());
    setOpen(false);
  };

  const formattedDisplay = useMemo(() => {
    if (!value) return "Select Session Date";
    const parts = value.split("-").map(Number);
    if (parts.length !== 3) return value;
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    return d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric", weekday: "short" });
  }, [value]);

  return (
    <div className="relative w-full text-left" ref={ref}>
      <button
        type="button"
        onClick={toggleOpen}
        className="w-full h-11 flex items-center justify-between gap-2 rounded-[13px] px-3.5 text-[13px] font-semibold border cursor-pointer outline-none transition-all shadow-sm"
        style={{
          background: open ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.8)",
          borderColor: error ? "#EF4444" : open ? T.olive : T.border,
          color: value ? "#1F1F1F" : "#888",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <span className="flex items-center gap-2.5 truncate">
          <FiCalendar size={15} style={{ color: open ? T.olive : T.orange }} />
          <span>{formattedDisplay}</span>
        </span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <FiChevronDown size={14} style={{ color: open ? T.olive : "#888" }} />
        </motion.div>
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[99999999]">
          <div className="fixed inset-0 bg-transparent" onClick={() => setOpen(false)} />
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="fixed w-[295px] rounded-[22px] p-3.5 shadow-2xl text-left bg-white border border-[#EFE5D8] z-[999999999]"
            style={{
              top: coords.top,
              left: coords.left,
              boxShadow: "0 22px 55px rgba(0,0,0,0.25)",
            }}
          >
            {/* Quick Action Chips */}
            <div className="flex items-center gap-1.5 mb-2.5 pb-2 border-b border-zinc-100">
              <button
                type="button"
                onClick={() => setQuickDate(0)}
                className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-[#6B8A5A]/10 text-[#6B8A5A] hover:bg-[#6B8A5A]/20 transition-all border-none cursor-pointer"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => setQuickDate(1)}
                className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-[#F29A0C]/10 text-[#F29A0C] hover:bg-[#F29A0C]/20 transition-all border-none cursor-pointer"
              >
                Tomorrow
              </button>
              <button
                type="button"
                onClick={() => setQuickDate(2)}
                className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-zinc-100 text-zinc-600 hover:bg-zinc-200 transition-all border-none cursor-pointer"
              >
                +2 Days
              </button>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between mb-2.5 px-1">
              <span className="text-[13px] font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {MONTHS_LIST[viewMonth]} {viewYear}
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={prevMonth}
                  disabled={new Date(viewYear, viewMonth - 1, 1) < new Date(effectiveMin.getFullYear(), effectiveMin.getMonth(), 1)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center border-none bg-zinc-100 hover:bg-zinc-200 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <FiChevronLeft size={14} />
                </button>
                <button type="button" onClick={nextMonth} className="w-7 h-7 rounded-lg flex items-center justify-center border-none bg-zinc-100 hover:bg-zinc-200 cursor-pointer">
                  <FiChevronRight size={14} />
                </button>
              </div>
            </div>

            {/* Weekday Labels */}
            <div className="grid grid-cols-7 gap-1 text-center mb-1">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(d => (
                <span key={d} className="text-[10px] font-black uppercase text-zinc-400">{d}</span>
              ))}
            </div>

            {/* Days Grid */}
            <div className="grid grid-cols-7 gap-1 text-center">
              {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                <span key={`empty-${i}`} />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const dayNum = i + 1;
                const mm = String(viewMonth + 1).padStart(2, "0");
                const dd = String(dayNum).padStart(2, "0");
                const dateStr = `${viewYear}-${mm}-${dd}`;
                const cellDate = new Date(viewYear, viewMonth, dayNum);
                cellDate.setHours(0, 0, 0, 0);
                const isPast = cellDate < effectiveMin;
                const isSelected = value === dateStr;

                return (
                  <button
                    key={dayNum}
                    type="button"
                    disabled={isPast}
                    onClick={() => !isPast && selectDay(dayNum)}
                    className="w-8 h-8 rounded-full text-[12px] font-bold flex items-center justify-center border-none transition-all m-auto"
                    style={{
                      background: isSelected ? T.olive : "transparent",
                      color: isSelected ? "#fff" : isPast ? "#ccc" : "#333",
                      boxShadow: isSelected ? "0 4px 12px rgba(107,138,90,0.4)" : "none",
                      opacity: isPast ? 0.35 : 1,
                      cursor: isPast ? "not-allowed" : "pointer",
                    }}
                  >
                    {dayNum}
                  </button>
                );
              })}
            </div>
          </motion.div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── CUSTOM LUXURY TIME PICKER COMPONENT (IST, BESPOKE GRIDS & REAL-TIME PAST VALIDATION) ───
function CustomTimePicker({ value, onChange, selectedDate = null, error = null }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const ref = useRef(null);

  // Parse 24-hr time "HH:mm" into 12-hr state
  const { hour12, minute, period } = useMemo(() => {
    if (!value) return { hour12: "06", minute: "00", period: "AM" };
    const [hStr, mStr] = value.split(":");
    let h = parseInt(hStr, 10);
    const m = mStr || "00";
    if (isNaN(h)) return { hour12: "06", minute: "00", period: "AM" };
    const p = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return { hour12: String(h).padStart(2, "0"), minute: m, period: p };
  }, [value]);

  const isSlotInPast = (h12, mStr, pStr) => {
    if (!selectedDate) return false;
    let h = parseInt(h12, 10);
    if (pStr === "PM" && h < 12) h += 12;
    if (pStr === "AM" && h === 12) h = 0;
    const [yr, mo, dy] = selectedDate.split("-").map(Number);
    if (!yr || !mo || !dy) return false;
    const target = new Date(yr, mo - 1, dy, h, parseInt(mStr, 10) || 0, 0, 0);
    return target <= new Date();
  };

  const isCurrentSelectionPast = useMemo(() => {
    if (!value || !selectedDate) return false;
    return isSlotInPast(hour12, minute, period);
  }, [value, selectedDate, hour12, minute, period]);

  const updatePosition = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const popupWidth = 320;
    const popupHeight = 390;

    // Prefer opening below button, but if it overflows bottom of screen, open above or clamp
    let top = rect.bottom + 6;
    if (top + popupHeight > window.innerHeight - 10) {
      const topAbove = rect.top - popupHeight - 6;
      top = topAbove >= 10 ? topAbove : Math.max(10, window.innerHeight - popupHeight - 10);
    }

    // Align right edge of popup with right edge of trigger button
    let left = rect.right - popupWidth;
    if (left < 10) left = Math.max(10, rect.left);
    if (left + popupWidth > window.innerWidth - 10) {
      left = Math.max(10, window.innerWidth - popupWidth - 10);
    }

    setCoords({ top, left });
  }, []);

  const toggleOpen = () => {
    if (!open) {
      updatePosition();
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (open) {
      updatePosition();
      const handleScrollOrResize = () => updatePosition();
      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);
      return () => {
        window.removeEventListener("scroll", handleScrollOrResize, true);
        window.removeEventListener("resize", handleScrollOrResize);
      };
    }
  }, [open, updatePosition]);

  const updateTime = (newH12, newM, newP) => {
    let h = parseInt(newH12, 10);
    if (newP === "PM" && h < 12) h += 12;
    if (newP === "AM" && h === 12) h = 0;
    const h24 = String(h).padStart(2, "0");
    const mStr = String(newM).padStart(2, "0");
    onChange(`${h24}:${mStr}`);
  };

  const presetSlots = [
    { label: "06:00 AM", title: "Sunrise Flow", h: "06", m: "00", p: "AM" },
    { label: "07:30 AM", title: "Morning Sadhana", h: "07", m: "30", p: "AM" },
    { label: "11:00 AM", title: "Pranayama Desk", h: "11", m: "00", p: "AM" },
    { label: "05:00 PM", title: "Sunset Flow", h: "05", m: "00", p: "PM" },
    { label: "06:30 PM", title: "Evening Meditation", h: "06", m: "30", p: "PM" },
    { label: "08:00 PM", title: "Yoga Nidra", h: "08", m: "00", p: "PM" },
  ];

  const formattedDisplay = useMemo(() => {
    if (!value) return "Select Start Time (IST)";
    return `${hour12}:${minute} ${period} (IST)`;
  }, [value, hour12, minute, period]);

  const hourOptions = ["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"];
  const minuteOptions = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];

  return (
    <div className="relative w-full text-left" ref={ref}>
      <button
        type="button"
        onClick={toggleOpen}
        className="w-full h-11 flex items-center justify-between gap-2 rounded-[13px] px-3.5 text-[13px] font-semibold border cursor-pointer outline-none transition-all shadow-sm"
        style={{
          background: open ? "rgba(255,255,255,0.98)" : "rgba(255,255,255,0.8)",
          borderColor: (error || isCurrentSelectionPast) ? "#EF4444" : open ? T.olive : T.border,
          color: value ? (isCurrentSelectionPast ? "#DC2626" : "#1F1F1F") : "#888",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <span className="flex items-center gap-2.5 truncate">
          <FiClock size={15} style={{ color: (error || isCurrentSelectionPast) ? "#EF4444" : open ? T.olive : T.orange }} />
          <span className="font-bold">{formattedDisplay}</span>
        </span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <FiChevronDown size={14} style={{ color: open ? T.olive : "#888" }} />
        </motion.div>
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[99999999]">
          <div className="fixed inset-0 bg-transparent" onClick={() => setOpen(false)} />
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="fixed w-[320px] rounded-[24px] p-4 shadow-2xl text-left bg-white border border-[#EFE5D8] z-[999999999]"
            style={{
              top: coords.top,
              left: coords.left,
              boxShadow: "0 25px 60px rgba(0,0,0,0.28)",
            }}
          >
            {/* Header with IST Badge */}
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-zinc-100">
              <span className="text-[12px] font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Class Schedule Time
              </span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F29A0C]/15 text-[#D97706] border border-[#F29A0C]/30 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F29A0C] animate-pulse" />
                IST (UTC+5:30)
              </span>
            </div>

            {/* Live Clock Display Card */}
            <div className="bg-gradient-to-br from-[#FFFDF9] to-[#FFF6E9] p-3 rounded-[16px] border border-[#F4D9B5] mb-3 text-center shadow-inner">
              <div className="flex items-center justify-center gap-2">
                <span className="text-[26px] font-black text-[#1F1F1F] tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {hour12}
                </span>
                <span className="text-[24px] font-black text-[#F29A0C] animate-pulse">:</span>
                <span className="text-[26px] font-black text-[#1F1F1F] tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {minute}
                </span>
                <div className="flex items-center gap-1 ml-2 bg-white/90 p-1 rounded-xl border border-[#F4D9B5] shadow-xs">
                  <button
                    type="button"
                    onClick={() => updateTime(hour12, minute, "AM")}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-extrabold cursor-pointer border-none transition-all ${
                      period === "AM" ? "bg-[#F29A0C] text-white shadow-sm" : "bg-transparent text-zinc-500 hover:text-black"
                    }`}
                  >
                    AM
                  </button>
                  <button
                    type="button"
                    onClick={() => updateTime(hour12, minute, "PM")}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-extrabold cursor-pointer border-none transition-all ${
                      period === "PM" ? "bg-[#6B8A5A] text-white shadow-sm" : "bg-transparent text-zinc-500 hover:text-black"
                    }`}
                  >
                    PM
                  </button>
                </div>
              </div>

              {/* Past time alert inside picker */}
              {isCurrentSelectionPast && (
                <div className="mt-2 text-[11px] font-bold text-red-600 bg-red-50 border border-red-200 rounded-lg py-1 px-2 flex items-center justify-center gap-1">
                  <FiAlertTriangle size={12} className="shrink-0 text-red-500" />
                  <span>Selected time has already passed for today!</span>
                </div>
              )}
            </div>

            {/* Custom Hour Grid (No Select Tags) */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-black uppercase text-zinc-400 tracking-wider">Select Hour</span>
                <span className="text-[10px] font-bold text-[#F29A0C]">{hour12} hr</span>
              </div>
              <div className="grid grid-cols-6 gap-1">
                {hourOptions.map((h) => {
                  const isSel = hour12 === h;
                  return (
                    <button
                      key={h}
                      type="button"
                      onClick={() => updateTime(h, minute, period)}
                      className={`h-7 rounded-[9px] text-[11px] font-black transition-all border cursor-pointer ${
                        isSel
                          ? "bg-[#F29A0C] text-white border-[#F29A0C] shadow-sm scale-105"
                          : "bg-[#FFFBF5] text-zinc-700 border-[#F4E6D2] hover:border-[#F29A0C] hover:bg-[#FFF3E0]"
                      }`}
                    >
                      {h}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom Minute Grid (No Select Tags) */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-black uppercase text-zinc-400 tracking-wider">Select Minute</span>
                <span className="text-[10px] font-bold text-[#6B8A5A]">{minute} min</span>
              </div>
              <div className="grid grid-cols-6 gap-1">
                {minuteOptions.map((m) => {
                  const isSel = minute === m;
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => updateTime(hour12, m, period)}
                      className={`h-7 rounded-[9px] text-[11px] font-black transition-all border cursor-pointer ${
                        isSel
                          ? "bg-[#6B8A5A] text-white border-[#6B8A5A] shadow-sm scale-105"
                          : "bg-[#F9FCF7] text-zinc-700 border-[#E2EBDC] hover:border-[#6B8A5A] hover:bg-[#EEF5E8]"
                      }`}
                    >
                      :{m}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Standard Yoga Batches Presets */}
            <div className="space-y-1.5 mb-3">
              <span className="text-[10px] font-black uppercase text-zinc-400 tracking-wider block">Standard Yoga Batches</span>
              <div className="grid grid-cols-2 gap-1.5">
                {presetSlots.map((slot) => {
                  const isCur = hour12 === slot.h && minute === slot.m && period === slot.p;
                  const isPast = isSlotInPast(slot.h, slot.m, slot.p);
                  return (
                    <button
                      key={slot.label}
                      type="button"
                      onClick={() => {
                        updateTime(slot.h, slot.m, slot.p);
                      }}
                      className={`p-1.5 rounded-[11px] text-left border cursor-pointer transition-all ${
                        isCur
                          ? "bg-[#6B8A5A]/15 border-[#6B8A5A] text-[#27500A] ring-1 ring-[#6B8A5A]"
                          : isPast
                          ? "bg-zinc-50 border-zinc-200 text-zinc-400 hover:bg-zinc-100"
                          : "bg-[#FFFBF5] border-[#F4E6D2] hover:bg-[#FFF3E0] text-zinc-700"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-black">{slot.label}</span>
                        {isPast && <span className="text-[8px] font-bold text-red-500 uppercase">Past</span>}
                      </div>
                      <div className="text-[9px] text-zinc-500 truncate">{slot.title}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Modal Controls */}
            <div className="flex items-center gap-2 pt-1 border-t border-zinc-100">
              <button
                type="button"
                onClick={() => {
                  onChange("");
                  setOpen(false);
                }}
                className="flex-1 py-2 rounded-xl bg-zinc-100 text-zinc-600 text-[11px] font-bold border-none cursor-pointer hover:bg-zinc-200 transition-colors"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex-2 py-2 rounded-xl text-white text-[11px] font-black border-none cursor-pointer transition-all shadow-md"
                style={{
                  background: isCurrentSelectionPast
                    ? "#DC2626"
                    : `linear-gradient(135deg, ${T.olive}, ${T.olive2})`,
                }}
              >
                {isCurrentSelectionPast ? "Time In Past (Change)" : "Confirm Time (IST)"}
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── SCHEDULE LIVE CLASS MODAL (WITH STRICT VALIDATION & CUSTOM PICKERS) ───────
function ScheduleSessionModal({ onClose, showToast, onSave, existingSessions = [] }) {
  const { tiers: dynamicTiers } = useDynamicPackages();
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // ALL FIELDS MUST BE EMPTY BY DEFAULT (NO DEFAULT VALUES)
  const [form, setForm] = useState({
    title: "",
    instructorName: "",
    maxCapacity: "",
    date: "",
    time: "",
    durationInMinutes: "",
    category: "",
    tier: "",
    description: ""
  });

  const tierOptions = useMemo(() => {
    const list = Array.isArray(dynamicTiers) && dynamicTiers.length > 0
      ? dynamicTiers
      : ["Free Trial", "Basic", "Standard", "Premium", "YTT Pro"];
    return [
      { value: "All", label: "All Tiers (Public Access)" },
      ...list.map(t => {
        const val = typeof t === "string" ? t : (t.name || t.packageType || String(t));
        return { value: val, label: val };
      })
    ];
  }, [dynamicTiers]);

  const categoryOptions = [
    { value: "Beginner Yoga", label: "Beginner Yoga" },
    { value: "Pranayama", label: "Pranayama" },
    { value: "Meditation", label: "Meditation" },
    { value: "YTT Module", label: "YTT Module" },
    { value: "Diet & Nutrition", label: "Diet & Nutrition" },
    { value: "Vinyasa Flow", label: "Vinyasa Flow" },
    { value: "Hatha Yoga", label: "Hatha Yoga" },
    { value: "Ashtanga", label: "Ashtanga" },
    { value: "Therapeutic & Spine", label: "Therapeutic & Spine" },
  ];

  const durationOptions = [
    { value: "15", label: "15 minutes" },
    { value: "30", label: "30 minutes" },
    { value: "45", label: "45 minutes (Standard)" },
    { value: "60", label: "60 minutes (1 Hour)" },
    { value: "90", label: "90 minutes (1.5 Hours)" },
    { value: "120", label: "120 minutes (2 Hours)" },
  ];

  // Capacity numeric sanitizer and stepper controls (strict positive integers)
  const handleCapacityChange = (val) => {
    const cleaned = String(val).replace(/[^0-9]/g, "");
    setForm(prev => ({ ...prev, maxCapacity: cleaned }));
    if (errors.maxCapacity) setErrors(prev => ({ ...prev, maxCapacity: null }));
  };

  const stepCapacity = (delta) => {
    const cur = parseInt(form.maxCapacity, 10) || 0;
    const nextVal = Math.min(10000, Math.max(1, cur + delta));
    setForm(prev => ({ ...prev, maxCapacity: String(nextVal) }));
    if (errors.maxCapacity) setErrors(prev => ({ ...prev, maxCapacity: null }));
  };

  const setPresetCapacity = (val) => {
    setForm(prev => ({ ...prev, maxCapacity: String(val) }));
    if (errors.maxCapacity) setErrors(prev => ({ ...prev, maxCapacity: null }));
  };

  // Helper to validate whether a selected date & time is in the past
  const checkIsPastDateTime = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return false;
    const [yr, mo, dy] = dateStr.split("-").map(Number);
    const [hh, mm] = timeStr.split(":").map(Number);
    if (!yr || !mo || !dy || isNaN(hh) || isNaN(mm)) return false;
    const target = new Date(yr, mo - 1, dy, hh, mm, 0, 0);
    return target <= new Date();
  };

  const handleDateChange = (v) => {
    setForm(prev => ({ ...prev, date: v }));
    if (errors.date) setErrors(prev => ({ ...prev, date: null }));
    if (v && form.time && checkIsPastDateTime(v, form.time)) {
      setErrors(prev => ({ ...prev, time: "Selected time has already passed for this date. Please select a future time slot." }));
    } else if (errors.time?.includes("passed")) {
      setErrors(prev => ({ ...prev, time: null }));
    }
  };

  const handleTimeChange = (v) => {
    setForm(prev => ({ ...prev, time: v }));
    if (form.date && v && checkIsPastDateTime(form.date, v)) {
      setErrors(prev => ({ ...prev, time: "Selected time has already passed for today's date. Please select a future time slot." }));
      showToast("Cannot select a past time for today's date");
    } else {
      setErrors(prev => ({ ...prev, time: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!form.title.trim()) {
      newErrors.title = "Class title is required";
    } else if (form.title.trim().length < 3) {
      newErrors.title = "Title must be at least 3 characters";
    }

    if (!form.instructorName.trim()) {
      newErrors.instructorName = "Instructor name is required";
    }

    if (!form.tier) {
      newErrors.tier = "Please select required membership tier";
    }

    if (!form.date) {
      newErrors.date = "Please select scheduled date";
    }

    if (!form.time) {
      newErrors.time = "Please select scheduled start time (IST)";
    }

    if (!form.durationInMinutes) {
      newErrors.durationInMinutes = "Please select class duration";
    }

    const capNum = parseInt(form.maxCapacity, 10);
    if (!form.maxCapacity || isNaN(capNum) || capNum < 1) {
      newErrors.maxCapacity = "Capacity must be a positive number (min 1)";
    } else if (capNum > 10000) {
      newErrors.maxCapacity = "Capacity cannot exceed 10,000";
    }

    if (!form.category) {
      newErrors.category = "Please select a category / discipline";
    }

    // Past date & time strict validation
    if (form.date && form.time && checkIsPastDateTime(form.date, form.time)) {
      newErrors.time = "Scheduled time cannot be in the past. Please select an upcoming time.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstErr = Object.values(newErrors)[0];
      showToast(firstErr);
      return;
    }

    // Combine date + time
    const [yr, mo, dy] = form.date.split("-").map(Number);
    const [hh, mm] = form.time.split(":").map(Number);
    const reqStart = new Date(yr, mo - 1, dy, hh, mm, 0, 0);
    if (isNaN(reqStart.getTime())) {
      showToast("Invalid date or time format");
      return;
    }

    const now = new Date();
    if (reqStart <= now) {
      showToast("Scheduled time cannot be in the past");
      setErrors(prev => ({ ...prev, time: "Scheduled time cannot be in the past" }));
      return;
    }

    const durMins = parseInt(form.durationInMinutes, 10) || 45;
    const reqEnd = new Date(reqStart.getTime() + durMins * 60000);
    const reqTier = (form.tier || "Free").trim().toLowerCase();

    // Overlap conflict check against existing sessions
    const conflict = (existingSessions || []).find(s => {
      if (s.status === "Cancelled" || s.isActive === false) return false;

      const sTier = (s.tier || "Free").trim().toLowerCase();
      const isTierOverlap = sTier === reqTier || sTier === "all" || reqTier === "all";
      if (!isTierOverlap) return false;

      const sStart = s.dateObj ? new Date(s.dateObj) : (s.scheduledAt ? new Date(s.scheduledAt) : null);
      if (!sStart || isNaN(sStart.getTime())) return false;
      const sDur = parseInt(s.duration || s.durationInMinutes || 45, 10);
      const sEnd = new Date(sStart.getTime() + sDur * 60000);

      return reqStart < sEnd && reqEnd > sStart;
    });

    if (conflict) {
      const sStart = conflict.dateObj ? new Date(conflict.dateObj) : new Date(conflict.scheduledAt);
      const sDur = parseInt(conflict.duration || conflict.durationInMinutes || 45, 10);
      const sEnd = new Date(sStart.getTime() + sDur * 60000);
      const formatTime = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
      const formatDate = (d) => d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });

      const overlapMsg = `A live session ('${conflict.title}') is already scheduled for ${conflict.tier} tier from ${formatTime(sStart)} to ${formatTime(sEnd)} on ${formatDate(sStart)}. Please choose a different time slot.`;
      showToast(overlapMsg);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        instructorName: form.instructorName.trim(),
        maxCapacity: capNum,
        scheduledAt: reqStart.toISOString(),
        durationInMinutes: durMins,
        category: form.category.trim(),
        tier: form.tier.trim(),
        description: form.description.trim()
      };

      const res = await liveSessionAPI.createSession(payload);
      if (res.data?.success) {
        showToast("✓ Live session class scheduled successfully!");
        onSave();
      } else {
        showToast(res.data?.message || "✓ Live session scheduled!");
        onSave();
      }
    } catch (err) {
      showToast(err.response?.data?.message || err.response?.data?.Message || err.message || "Failed to schedule session");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[99999] bg-black/50 backdrop-blur-[8px]" onClick={onClose}/>
      <div className="ad-modal ad-modal-scroll fixed top-1/2 left-1/2 z-[999999] w-[92%] max-w-[620px] max-h-[90vh]"
        style={{ transform: "translate(-50%, -50%)" }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 15 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          className="w-full rounded-[30px] overflow-hidden flex flex-col shadow-2xl bg-[#FFFDFB] border border-[#F4D9B5]"
        >
          {/* Header */}
          <div className="px-6 sm:px-8 py-4.5 flex items-center justify-between border-b border-[#F4D9B5]/70 bg-gradient-to-r from-[#FFF8EE] via-white to-[#FFF8EE]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#F29A0C] to-[#E97818] text-white flex items-center justify-center text-lg shadow-md shadow-[#F29A0C]/30">
                <FiCalendar />
              </div>
              <div>
                <h3 className="text-[17px] font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Schedule Live Session
                </h3>
                <p className="text-[11px] text-[#666666] font-medium mt-0.5">
                  Set up live interactive studio batch in Indian Standard Time (IST)
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-xl flex items-center justify-center bg-zinc-100 hover:bg-zinc-200 border-none cursor-pointer text-zinc-600 transition-colors"
            >
              <FiX size={16}/>
            </button>
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="overflow-y-auto p-6 sm:p-7 max-h-[calc(90vh-140px)] scrollbar-thin flex flex-col gap-4 text-left">
            
            {/* Title */}
            <div>
              <label className="block text-[11px] font-black uppercase tracking-wider text-[#555555] mb-1.5">
                Session Title <span className="text-red-500">*</span>
              </label>
              <input 
                type="text" 
                placeholder="e.g. Sunrise Hatha Yoga Live Flow" 
                value={form.title} 
                onChange={e => {
                  setForm({...form, title: e.target.value});
                  if (errors.title) setErrors(prev => ({ ...prev, title: null }));
                }}
                className={`w-full h-11 px-3.5 rounded-[13px] border bg-white focus:outline-none text-[13px] text-[#1F1F1F] font-semibold transition-all shadow-sm ${
                  errors.title ? "border-red-500 bg-red-50/30" : "border-[#EFE5D8] focus:border-[#F29A0C]"
                }`}
              />
              {errors.title && (
                <p className="text-[11px] font-bold text-red-500 mt-1 flex items-center gap-1">
                  <FiAlertTriangle size={11} /> {errors.title}
                </p>
              )}
            </div>

            {/* Instructor & Tier */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-[#555555] mb-1.5">
                  Instructor Name <span className="text-red-500">*</span>
                </label>
                <input 
                  type="text" 
                  placeholder="e.g. Acharya Ramanand" 
                  value={form.instructorName} 
                  onChange={e => {
                    setForm({...form, instructorName: e.target.value});
                    if (errors.instructorName) setErrors(prev => ({ ...prev, instructorName: null }));
                  }}
                  className={`w-full h-11 px-3.5 rounded-[13px] border bg-white focus:outline-none text-[13px] text-[#1F1F1F] font-semibold transition-all shadow-sm ${
                    errors.instructorName ? "border-red-500 bg-red-50/30" : "border-[#EFE5D8] focus:border-[#F29A0C]"
                  }`}
                />
                {errors.instructorName && (
                  <p className="text-[11px] font-bold text-red-500 mt-1 flex items-center gap-1">
                    <FiAlertTriangle size={11} /> {errors.instructorName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-[#555555] mb-1.5">
                  Required Membership Tier <span className="text-red-500">*</span>
                </label>
                <CustomDropdown
                  options={tierOptions}
                  value={form.tier}
                  onChange={v => {
                    setForm({ ...form, tier: v });
                    if (errors.tier) setErrors(prev => ({ ...prev, tier: null }));
                  }}
                  placeholder="Select Tier Eligibility"
                />
                {errors.tier && (
                  <p className="text-[11px] font-bold text-red-500 mt-1 flex items-center gap-1">
                    <FiAlertTriangle size={11} /> {errors.tier}
                  </p>
                )}
              </div>
            </div>

            {/* Custom Date & Custom Time Pickers (NO DEFAULT BROWSER PICKERS) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-[#555555] mb-1.5">
                  Scheduled Date <span className="text-red-500">*</span>
                </label>
                <CustomDatePicker
                  value={form.date}
                  onChange={handleDateChange}
                  error={errors.date}
                />
                {errors.date && (
                  <p className="text-[11px] font-bold text-red-500 mt-1 flex items-center gap-1">
                    <FiAlertTriangle size={11} /> {errors.date}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-[#555555] mb-1.5">
                  Start Time (IST) <span className="text-red-500">*</span>
                </label>
                <CustomTimePicker
                  value={form.time}
                  selectedDate={form.date}
                  onChange={handleTimeChange}
                  error={errors.time}
                />
                {errors.time && (
                  <p className="text-[11px] font-bold text-red-500 mt-1 flex items-center gap-1">
                    <FiAlertTriangle size={11} /> {errors.time}
                  </p>
                )}
              </div>
            </div>

            {/* Duration & Strictly Validated Capacity Stepper */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-[#555555] mb-1.5">
                  Class Duration <span className="text-red-500">*</span>
                </label>
                <CustomDropdown
                  options={durationOptions}
                  value={form.durationInMinutes}
                  onChange={v => {
                    setForm({ ...form, durationInMinutes: v });
                    if (errors.durationInMinutes) setErrors(prev => ({ ...prev, durationInMinutes: null }));
                  }}
                  placeholder="Select Duration"
                />
                {errors.durationInMinutes && (
                  <p className="text-[11px] font-bold text-red-500 mt-1 flex items-center gap-1">
                    <FiAlertTriangle size={11} /> {errors.durationInMinutes}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[11px] font-black uppercase tracking-wider text-[#555555] mb-1.5">
                  Max Capacity (Students) <span className="text-red-500">*</span>
                </label>
                
                {/* Stepper Input with Strict Positive Numbers */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => stepCapacity(-10)}
                    className="w-10 h-11 rounded-[12px] bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-black text-sm flex items-center justify-center border border-zinc-200 cursor-pointer transition-all active:scale-95"
                    title="Decrease by 10"
                  >
                    <FiMinus size={14} />
                  </button>

                  <input 
                    type="text" 
                    inputMode="numeric"
                    placeholder="e.g. 50" 
                    value={form.maxCapacity} 
                    onChange={e => handleCapacityChange(e.target.value)}
                    className={`flex-1 h-11 px-3 text-center rounded-[13px] border bg-white focus:outline-none text-[14px] text-[#1F1F1F] font-black transition-all shadow-sm ${
                      errors.maxCapacity ? "border-red-500 bg-red-50/30" : "border-[#EFE5D8] focus:border-[#F29A0C]"
                    }`}
                  />

                  <button
                    type="button"
                    onClick={() => stepCapacity(10)}
                    className="w-10 h-11 rounded-[12px] bg-zinc-100 hover:bg-zinc-200 text-zinc-700 font-black text-sm flex items-center justify-center border border-zinc-200 cursor-pointer transition-all active:scale-95"
                    title="Increase by 10"
                  >
                    <FiPlus size={14} />
                  </button>
                </div>

                {/* Preset Chips */}
                <div className="flex items-center gap-1.5 mt-2">
                  <span className="text-[10px] font-bold text-zinc-400 mr-1">Presets:</span>
                  {[25, 50, 100, 250, 500].map(val => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setPresetCapacity(val)}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold border cursor-pointer transition-all ${
                        String(form.maxCapacity) === String(val)
                          ? "bg-[#F29A0C] text-white border-[#F29A0C] shadow-sm"
                          : "bg-white text-zinc-600 border-zinc-200 hover:border-[#F29A0C]"
                      }`}
                    >
                      {val}
                    </button>
                  ))}
                </div>

                {errors.maxCapacity && (
                  <p className="text-[11px] font-bold text-red-500 mt-1 flex items-center gap-1">
                    <FiAlertTriangle size={11} /> {errors.maxCapacity}
                  </p>
                )}
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="block text-[11px] font-black uppercase tracking-wider text-[#555555] mb-1.5">
                Discipline / Category <span className="text-red-500">*</span>
              </label>
              <CustomDropdown
                options={categoryOptions}
                value={form.category}
                onChange={v => {
                  setForm({ ...form, category: v });
                  if (errors.category) setErrors(prev => ({ ...prev, category: null }));
                }}
                placeholder="Select Yoga Discipline"
              />
              {errors.category && (
                <p className="text-[11px] font-bold text-red-500 mt-1 flex items-center gap-1">
                  <FiAlertTriangle size={11} /> {errors.category}
                </p>
              )}
            </div>

            {/* Description / Syllabus */}
            <div>
              <label className="block text-[11px] font-black uppercase tracking-wider text-[#555555] mb-1.5">
                Class Syllabus & Outline
              </label>
              <textarea 
                rows={3} 
                placeholder="Describe asanas, breathing techniques, and guidance for participants..." 
                value={form.description} 
                onChange={e => setForm({...form, description: e.target.value})}
                className="w-full p-3 rounded-[13px] border border-[#EFE5D8] bg-white focus:outline-none focus:border-[#F29A0C] text-[13px] text-[#1F1F1F] font-semibold resize-none shadow-sm" 
              />
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#F4D9B5]/60 mt-1">
              <button
                type="button"
                onClick={onClose}
                className="h-10 px-5 rounded-full text-[12px] font-bold bg-zinc-100 hover:bg-zinc-200 border-none cursor-pointer text-zinc-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="h-10 px-7 rounded-full text-white text-[12px] font-black border-none cursor-pointer flex items-center gap-2 shadow-lg shadow-[#6B8A5A]/30 transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  background: `linear-gradient(135deg, ${T.olive}, ${T.olive2})`,
                  opacity: saving ? 0.6 : 1
                }}
              >
                {saving ? "Scheduling..." : "Schedule Live Class"}
              </button>
            </div>

          </form>
        </motion.div>
      </div>
    </>,
    document.body
  );
}

// ─── ASSIGN DIET PLAN MODAL ──────────────────────────────────────────────────
function AssignDietPlanModal({ users = [], onClose, showToast }) {
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  useEffect(() => {
    const fetchPublishedPlans = async () => {
      try {
        setLoadingPlans(true);
        const res = await dietPlanAPI.getAdminPlans();
        if (res.data?.success && Array.isArray(res.data.data)) {
          const published = res.data.data.filter(p => (p.status || p.Status) === "Published" || !p.status);
          setPlans(published);
        }
      } catch (err) {
        console.warn("Failed to load admin diet plans", err);
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPublishedPlans();
  }, []);

  const userOptions = useMemo(() => {
    return (users || []).map(u => ({
      value: String(u.id ?? u.Id),
      label: `${u.fullName || u.FullName || u.userName || "User"} (@${u.userName || u.UserName || "user"})`
    }));
  }, [users]);

  const planOptions = useMemo(() => {
    if (plans.length > 0) {
      return plans.map(p => ({
        value: p.name || p.Name,
        label: `${p.name || p.Name} (${p.tier || p.Tier || 'All Tiers'} · ${p.totalCal || p.totalCalories || 0} kcal)`
      }));
    }
    return [
      { value: "Free Wellness Plan", label: "Free Wellness Plan (Free · 1800 kcal)" },
      { value: "Basic Wellness Plan", label: "Basic Wellness Plan (Basic · 2000 kcal)" },
      { value: "Standard Wellness Plan", label: "Standard Wellness Plan (Standard · 2200 kcal)" },
      { value: "Premium Wellness Plan", label: "Premium Wellness Plan (Premium · 2400 kcal)" },
      { value: "YTT Pro Wellness Plan", label: "YTT Pro Wellness Plan (Pro · 2500 kcal)" },
    ];
  }, [plans]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedUser) {
      showToast("Please select a registered user to assign the plan to");
      return;
    }
    if (!selectedPlan) {
      showToast("Please select a wellness diet plan");
      return;
    }

    const matchedUser = (users || []).find(u => String(u.id ?? u.Id) === String(selectedUser));
    const userName = matchedUser ? (matchedUser.fullName || matchedUser.FullName || matchedUser.userName) : "User";
    
    // Save to local storage & cache
    const assignments = JSON.parse(localStorage.getItem("assigned_diets") || "{}");
    assignments[selectedUser] = selectedPlan;
    localStorage.setItem("assigned_diets", JSON.stringify(assignments));

    showToast(`✓ Assigned "${selectedPlan}" to "${userName}" successfully!`);
    onClose();
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[99999] bg-black/40 backdrop-blur-[6px]" onClick={onClose}/>
      <div className="ad-modal ad-modal-scroll fixed top-1/2 left-1/2 z-[999999] w-[90%] max-w-[480px] h-[auto] max-h-[480px]"
        style={{ transform: "translate(-50%, -50%)" }}>
        <motion.div initial={{ opacity:0, scale:0.95 }} animate={{ opacity:1, scale:1 }} exit={{ opacity:0, scale:0.95 }}
          className="w-full h-full rounded-[28px] overflow-hidden flex flex-col shadow-2xl bg-white/95"
          style={glass}>
          <div className="px-6 py-4 flex items-center justify-between border-b" style={{ borderColor: T.border }}>
            <div>
              <h3 className="text-[17px] font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins', sans-serif" }}>Assign Diet Plan</h3>
              <p className="text-[11px] text-zinc-400 mt-0.5">Assign custom nutrition plan to user</p>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-[10px] flex items-center justify-center bg-zinc-100 hover:bg-zinc-200 border-none cursor-pointer text-zinc-500">
              <FiX size={15}/>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 p-6 flex flex-col gap-4 text-left">
            <div>
              <label className="block text-[11px] font-bold text-zinc-500 uppercase tracking-wide mb-1.5">
                Select User <span className="text-red-500">*</span>
              </label>
              <CustomDropdown
                options={userOptions}
                value={selectedUser}
                onChange={v => setSelectedUser(v)}
                placeholder="-- Choose User --"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-zinc-500 uppercase tracking-wide mb-1.5">
                Select Wellness Diet Plan <span className="text-red-500">*</span>
              </label>
              <CustomDropdown
                options={planOptions}
                value={selectedPlan}
                onChange={v => setSelectedPlan(v)}
                placeholder={loadingPlans ? "-- Loading published plans... --" : "-- Choose Published Diet Plan --"}
              />
            </div>

            <div className="flex gap-2 justify-end mt-auto pt-4 border-t" style={{ borderColor: T.border }}>
              <button type="button" onClick={onClose} className="h-10 px-5 rounded-full text-[12px] font-bold bg-zinc-100 hover:bg-zinc-200 border-none cursor-pointer text-zinc-500">
                Cancel
              </button>
              <button type="submit"
                className="h-10 px-6 rounded-full text-white text-[12px] font-bold border-none cursor-pointer"
                style={{ background: `linear-gradient(135deg, ${T.olive}, ${T.olive2})` }}>
                Assign Plan
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </>,
    document.body
  );
}

// ─── BULK NOTIFICATION MODAL ────────────────────────────────────────────────
function BulkNotificationModal({ onClose, showToast }) {
  const { tiers: dynamicTiers } = useDynamicPackages();
  const [audience, setAudience] = useState("");
  const [channel, setChannel] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const audienceOptions = useMemo(() => {
    const staticSegments = [
      { value: "All Users", label: "All Users (Broadcast)" },
      { value: "Expiring Soon", label: "Expiring Soon" },
      { value: "Inactive 14d+", label: "Inactive 14d+" },
      { value: "New Registrations", label: "New Registrations" },
    ];
    const tierSegments = (dynamicTiers || []).map(t => {
      const name = typeof t === "string" ? t : (t.name || t.packageType || String(t));
      return { value: `${name} Tier Users`, label: `${name} Tier Users` };
    });
    return [...staticSegments, ...tierSegments];
  }, [dynamicTiers]);

  const channelOptions = [
    { value: "push", label: "Push Notification" },
    { value: "email", label: "Email Campaign" },
    { value: "both", label: "Both (Push + Email)" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!audience) {
      showToast("Please select a target audience");
      return;
    }
    if (!channel) {
      showToast("Please select a delivery channel");
      return;
    }
    if (!title.trim()) {
      showToast("Notification title is required");
      return;
    }
    if (!body.trim()) {
      showToast("Notification message body is required");
      return;
    }

    setSending(true);
    try {
      const res = await notificationAPI.sendNotification({
        title: title.trim(),
        message: body.trim(),
        audience: audience
      });
      if (res.data?.success || res.success) {
        showToast(`✓ Broadcast queued for segment "${audience}" via ${channel}!`);
        onClose();
      } else {
        showToast(res.data?.message || `✓ Broadcast queued for segment "${audience}" via ${channel}!`);
        onClose();
      }
    } catch (err) {
      showToast(`Error: ${err.response?.data?.message || err.response?.data?.Message || err.message || "Failed to broadcast notification"}`);
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[99999] bg-black/40 backdrop-blur-[6px]" onClick={onClose}/>
      <div className="ad-modal ad-modal-scroll fixed top-1/2 left-1/2 z-[999999] w-[90%] max-w-[500px] h-[auto] max-h-[540px]"
        style={{ transform: "translate(-50%, -50%)" }}>
        <motion.div initial={{ opacity:0, scale:0.95 }} animate={{ opacity:1, scale:1 }} exit={{ opacity:0, scale:0.95 }}
          className="w-full h-full rounded-[28px] overflow-hidden flex flex-col shadow-2xl bg-white/95"
          style={glass}>
          <div className="px-6 py-4 flex items-center justify-between border-b" style={{ borderColor: T.border }}>
            <div>
              <h3 className="text-[17px] font-black text-[#1F1F1F]" style={{ fontFamily: "'Poppins', sans-serif" }}>Send Broadcast Alert</h3>
              <p className="text-[11px] text-zinc-400 mt-0.5">Push alerts & email broadcasts to audience segments</p>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-[10px] flex items-center justify-center bg-zinc-100 hover:bg-zinc-200 border-none cursor-pointer text-zinc-500">
              <FiX size={15}/>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="ad-pad flex-1 p-6 flex flex-col gap-4 text-left">
            <div className="ad-1col grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-bold text-zinc-500 uppercase tracking-wide mb-1.5">
                  Target Audience <span className="text-red-500">*</span>
                </label>
                <CustomDropdown
                  options={audienceOptions}
                  value={audience}
                  onChange={v => setAudience(v)}
                  placeholder="Select Audience"
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-zinc-500 uppercase tracking-wide mb-1.5">
                  Channel <span className="text-red-500">*</span>
                </label>
                <CustomDropdown
                  options={channelOptions}
                  value={channel}
                  onChange={v => setChannel(v)}
                  placeholder="Select Channel"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-zinc-500 uppercase tracking-wide mb-1.5">
                Alert Header <span className="text-red-500">*</span>
              </label>
              <input 
                type="text" 
                placeholder="e.g. Special Weekend Wellness Live Session" 
                value={title} 
                onChange={e => setTitle(e.target.value)}
                className="w-full h-10 px-3.5 rounded-[12px] border bg-white/60 focus:bg-white focus:outline-none text-[13px] text-[#1F1F1F] font-semibold" 
                style={{ borderColor: T.border }}
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-zinc-500 uppercase tracking-wide mb-1.5">
                Message Content <span className="text-red-500">*</span>
              </label>
              <textarea 
                rows={4} 
                placeholder="Type broadcast message details here..." 
                value={body} 
                onChange={e => setBody(e.target.value)}
                className="w-full p-3 rounded-[12px] border bg-white/60 focus:bg-white focus:outline-none text-[13px] text-[#1F1F1F] font-semibold resize-none" 
                style={{ borderColor: T.border }}
              />
            </div>

            <div className="flex gap-2 justify-end mt-auto pt-4 border-t" style={{ borderColor: T.border }}>
              <button type="button" onClick={onClose} className="h-10 px-5 rounded-full text-[12px] font-bold bg-zinc-100 hover:bg-zinc-200 border-none cursor-pointer text-zinc-500">
                Cancel
              </button>
              <button type="submit" disabled={sending}
                className="h-10 px-6 rounded-full text-white text-[12px] font-bold border-none cursor-pointer flex items-center gap-2"
                style={{ background: `linear-gradient(135deg, ${T.olive}, ${T.olive2})`, opacity: sending ? 0.6 : 1 }}>
                {sending ? "Queueing..." : "Queue Broadcast"}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </>,
    document.body
  );
}